import type { AdvScene, AdvLine, AdvChoice, AdvCollagePage } from './adv'
import type { CampaignState, EndingChoice } from './types'
import { applyAdvVoiceV2 } from './adv_voice_v2'
import { applyAdvWordingFix } from './adv_wording_fix'

// 幕間 (戦闘後ADV) の本文。 ワークブック確定稿 (2026-07-01 全編執筆完了時点) の一字一句移植。
//   former-princess (箱3・先代の姫・特殊多段エンディング) は 2026-07-07 確定稿へ全面書き換え済 (Task2)。
const RAW_INTERLUDE: Record<string, AdvLine[]> = {
  'nameless-shade': [
    { kind: 'narration', text: '影は、霧のようにほどけて、消えた。' },
    { kind: 'lunaire', text: '——できた。' },
    { kind: 'voice', text: '——そう。あなたなら、できる。' },
    { kind: 'narration', text: '廊下の奥で、何かが目を覚ましたようだった。' },
  ],
  'dream-hound': [
    // 2026-10-27 下書き3版＝docs/research/2026-10-27-hound-adv-draft.md（試作で作者が通した台詞と絵のまま）。
    //   暗転から倒れた獣の絵を出し、 窓を出さずに約2秒、 床の王冠まで見せてからゆっくり引く。 光は画面に足さない。
    { kind: 'picture', src: '/adv/hound/beast_fallen.webp', fadeMs: 800 },
    { kind: 'stage', effect: 'zoom', scale: 1.08, x: .5, y: .5, ms: 1 }, { kind: 'stage', effect: 'zoom', scale: 1, x: .5, y: .5, ms: 4200 },
    { kind: 'cue', effect: 'sfx:adv_hound_battle_fade' }, { kind: 'cue', effect: 'amb:adv_hound_hall_air' },
    { kind: 'beat', ms: 900 },
    { kind: 'cue', effect: 'sfx:adv_hound_chain_small' },
    { kind: 'narration', text: '階段の下に、獣が倒れていた。', pre: 1500 },
    { kind: 'narration', text: '光が、ほどけて、どこかへ流れていった。' },
    { kind: 'lunaire', speech: 'spoken', text: 'もう、伏せてていいよ。' },
    { kind: 'lunaire', speech: 'inner', text: '……これで、楽になれた。' },
    { kind: 'voice', text: '——そう。あなたなら、できる。' },
  ],
  'bride': [
    // 2026-10-27 下書き3版＝docs/research/2026-10-26-bride-adv-draft.md（花嫁の体は画面の下の外・針がずれた絵へ差し替え）
    //   演出＝docs/research/2026-10-27-adv-emotion-staging.md。 揺らさない。 終わったあとはゆっくり引き、 針で時計へゆっくり寄る。
    { kind: 'picture', src: '/adv/bride/hall_full.webp' }, { kind: 'cue', effect: 'amb:adv_bride_hall_air' },   // 音の下敷き (2026-10-28)
    { kind: 'stage', effect: 'zoom', scale: 1.2, y: .38, ms: 1 }, { kind: 'stage', effect: 'zoom', scale: 1, ms: 3200 },
    { kind: 'narration', text: '花嫁は、上座の椅子の脇に崩れていた。', pre: 500 },
    { kind: 'lunaire', speech: 'spoken', text: '待たせて、ごめんね。' },
    { kind: 'lunaire', speech: 'inner', text: '……これで、楽になれた。' },
    { kind: 'picture', src: '/adv/bride/hall_shifted.webp' },
    { kind: 'stage', effect: 'zoom', scale: 1.45, y: .12, ms: 2600 },
    { kind: 'narration', text: '大時計の針が、少しずれていた。' },
  ],
  'vigil-knight': [
    // 2026-10-28 下書き5版＝docs/research/2026-10-27-knight-adv-draft.md（試作で作者が通した台詞と絵のまま）。音 (adv_knight_*) = 2026-10-28 scripts/render_adv_sfx.py (SOUNDS_KA)。
    //   膝をついた騎士からゆっくり引く。 槍が倒れる所だけ、 絵を人物絵として立てて少し沈める (後ろは黒)。
    { kind: 'picture', src: '/adv/knight/knight_kneel.webp', fadeMs: 800 },
    { kind: 'stage', effect: 'zoom', scale: 1.08, x: .5, y: .5, ms: 1 }, { kind: 'stage', effect: 'zoom', scale: 1, x: .5, y: .5, ms: 4200 },
    { kind: 'cue', effect: 'sfx:adv_knight_battle_fade' }, { kind: 'cue', effect: 'amb:adv_knight_hall_air' },
    { kind: 'beat', ms: 900 },
    { kind: 'cue', effect: 'sfx:exec_unique_knight_settle' },
    { kind: 'narration', text: '騎士は、槍を支えに膝をついた。', pre: 900 },
    { kind: 'narration', text: '兜は、扉のほうを向いたままだった。' },
    { kind: 'narration', text: '光が、ほどけて、どこかへ流れていった。' },
    { kind: 'lunaire', speech: 'spoken', text: '槍、重かったね。' },
    { kind: 'lunaire', speech: 'inner', text: '……これで、主のもとへ、行かせてあげられた……' },
    { kind: 'voice', text: '——そう。あなたなら、できる。' },
    { kind: 'cue', effect: 'sfx:adv_knight_lance_fall' },
    { kind: 'beat', ms: 120 },
    { kind: 'picture', src: '/adv/knight/black.webp', fadeMs: 0 },
    { kind: 'figure', src: '/adv/knight/lance_fallen.webp', x: .5, bottom: 1, height: 1, fadeMs: 0 },
    { kind: 'stage', effect: 'sink', power: 5, ms: 600 },
    { kind: 'narration', text: '槍が、手から離れて、床に倒れた。', pre: 380 },
  ],
  'the-executioner': [
    // 2026-10-27 版4＝docs/research/2026-10-27-executioner-adv-draft.md（試作で作者が通した台詞と絵のまま）。
    //   暗転から倒れた男の絵を出し、 窓を出さずに約2秒見せてからゆっくり引く。 光は画面に足さない。
    //   音 (adv_exec_*) = 2026-10-28 scripts/render_adv_sfx.py。
    { kind: 'picture', src: '/adv/executioner/fallen_floor.webp', fadeMs: 800 },
    { kind: 'stage', effect: 'zoom', scale: 1.08, x: .5, y: .5, ms: 1 }, { kind: 'stage', effect: 'zoom', scale: 1, x: .5, y: .5, ms: 4200 },
    { kind: 'cue', effect: 'sfx:adv_exec_battle_fade' }, { kind: 'cue', effect: 'amb:adv_exec_hall_air' },
    { kind: 'narration', text: '男が、石の床に倒れていた。', pre: 2400 },
    { kind: 'narration', text: '刃の腕は、倒れてなお、構えを解かなかった。' },
    { kind: 'narration', text: '光が、ほどけて、どこかへ流れていった。' },
    { kind: 'stage', effect: 'insert', src: '/adv/executioner/verdict_floor.webp', fadeMs: 400 },
    { kind: 'cue', effect: 'sfx:adv_exec_paper_floor' },
    { kind: 'narration', text: '懐から、判決書がこぼれていた。' },
    { kind: 'stage', effect: 'insert', src: null },
    { kind: 'lunaire', speech: 'spoken', text: '読み上げは、おしまい。' },
    { kind: 'lunaire', speech: 'inner', text: '……これで、楽になれた。' },
    { kind: 'narration', text: 'レイピアを握る手が、よく馴染んでいた。何度もやってきた動きみたいに。' },
    // 考え・悩みの行は「。」で閉じない (作者 10-27・LUNAIRE 共通)。
    { kind: 'lunaire', speech: 'inner', text: '私も、一つずつ、終わらせてきた……' },
    { kind: 'lunaire', speech: 'spoken', text: '最後まで、やるね。' },
    { kind: 'voice', text: '——そう。あなたなら、できる。' },
  ],
  'crowned-king-awoken': [
    // 2026-10-28 下書き5版＝docs/research/2026-10-27-awoken-adv-draft.md（試作で作者が通した台詞と絵のまま）。
    //   戴冠獣と同じ伏せ方と同じ言葉で、同じ獣だと分からせる。 獣の顔は描かない。 青い灯の絵は無音で見せる。
    //   音 (adv_awoken_*) = 2026-10-28 scripts/render_adv_sfx.py (SOUNDS_KA)。 獣の声は戦いの声 (awoken_voice_haunt_*) のまま。
    { kind: 'picture', src: '/adv/awoken/beast_fallen.webp', fadeMs: 1500 },
    { kind: 'stage', effect: 'zoom', scale: 1.08, x: .5, y: .5, ms: 1 }, { kind: 'stage', effect: 'zoom', scale: 1, x: .5, y: .5, ms: 5000 },
    { kind: 'cue', effect: 'sfx:adv_awoken_battle_fade' },
    { kind: 'beat', ms: 1200 },
    { kind: 'cue', effect: 'amb:adv_awoken_hall_air' },
    { kind: 'narration', text: '獣が、石の山のふもとに倒れていた。', pre: 1200 },
    { kind: 'cue', effect: 'sfx:adv_awoken_claw_scrape' },
    { kind: 'narration', text: '前足が、床を掻いた。' },
    { kind: 'stage', effect: 'zoom', scale: 1.05, x: .894, y: .5, ms: 2600 },
    { kind: 'cue', effect: 'sfx:adv_awoken_drag' }, { kind: 'stage', effect: 'shake', power: 3, ms: 1200 },
    { kind: 'narration', text: '体を引きずって、石の山のほうへ這っていった。', pre: 400 },
    { kind: 'narration', text: '埋もれた玉座の前で、止まった。' },
    { kind: 'beat', ms: 600 },
    { kind: 'picture', src: '/adv/awoken/beast_rest.webp', fadeMs: 1400 },
    { kind: 'stage', effect: 'zoom', scale: 1, x: .5, y: .733, ms: 1 }, { kind: 'stage', effect: 'zoom', scale: 1.05, x: .5, y: .733, ms: 5000 },
    { kind: 'cue', effect: 'sfx:adv_awoken_lay_down' },
    // 戴冠獣の戦いの前と同じ一文と、 戴冠獣が最初に言った言葉。
    { kind: 'narration', text: '頭を下げ、前足の上に載せた。', pre: 1500 },
    { kind: 'cue', effect: 'sfx:awoken_voice_haunt_huff' },
    { kind: 'boss', name: '獣', text: '主の、……前。', pre: 800 },
    { kind: 'narration', text: 'あの広間で、聞いた言葉だった。', pre: 900 },
    { kind: 'lunaire', speech: 'inner', text: '……別の獣じゃ、なかった……' },
    { kind: 'lunaire', speech: 'spoken', text: '主のこと、覚えてたんだね。', pre: 700 },
    { kind: 'narration', text: '割れた王冠から、光がこぼれていた。' },
    { kind: 'narration', text: '光が、ほどけて、流れていった。' },
    { kind: 'beat', ms: 700 },
    { kind: 'picture', src: '/adv/awoken/blue_lamp.webp', fadeMs: 1500 },
    { kind: 'cue', effect: 'amb:off' },
    { kind: 'narration', text: '流れた先で、青い灯が、ひとつ、灯っていた。', pre: 1600 },
    { kind: 'beat', ms: 1800 },
    // 言い終わる前に、 声がさえぎる (auto で次の行へ)。
    { kind: 'picture', src: '/adv/awoken/beast_rest.webp', fadeMs: 900 },
    { kind: 'stage', effect: 'zoom', scale: 1, x: .5, y: .5, ms: 1 },
    { kind: 'cue', effect: 'amb:adv_awoken_hall_air' },
    { kind: 'lunaire', speech: 'spoken', text: 'これで、楽に——', pre: 1000, auto: 350 },
    { kind: 'voice', text: '——そう。あなたなら、できる。' },
  ],
  'inverted-jester': [
    // 2026-10-27 下書き5版＝docs/research/2026-10-27-jester-adv-draft.md（試作で作者が通した台詞と絵のまま）。
    //   言いかけた「楽に——」を、 数える声がさえぎる (風を消して無音)。 光は画面に足さない。
    //   音 (adv_jester_*) = 2026-10-28 scripts/render_adv_sfx.py。
    { kind: 'picture', src: '/adv/jester/fallen.webp', fadeMs: 800 },
    { kind: 'stage', effect: 'zoom', scale: 1.08, x: .5, y: .5, ms: 1 }, { kind: 'stage', effect: 'zoom', scale: 1, x: .5, y: .5, ms: 4200 },
    { kind: 'cue', effect: 'sfx:adv_jester_battle_fade' }, { kind: 'cue', effect: 'amb:adv_jester_wind' },
    { kind: 'narration', text: '鐘の下に、獣が倒れていた。', pre: 1300 },
    { kind: 'narration', text: '光が、ほどけて、どこかへ流れていった。' },
    { kind: 'narration', text: '倒れたまま、獣はまだ、小さく数えていた。' },
    { kind: 'boss', name: '鶏', text: '……ひとつ。……ふたつ。' },
    { kind: 'lunaire', speech: 'inner', text: '……これで、楽に——' },
    { kind: 'cue', effect: 'amb:off' },
    { kind: 'narration', text: '数える声は、止まらなかった。', pre: 500 },
    { kind: 'lunaire', speech: 'spoken', text: 'まだ、数えてるね。' },
    { kind: 'lunaire', speech: 'inner', text: '私も、数えてる……？　終わらせた数を……' },
    { kind: 'lunaire', speech: 'spoken', text: '数え終わるまで、ここにいる。' },
    { kind: 'cue', effect: 'amb:adv_jester_wind' },
    { kind: 'voice', text: '——そう。あなたなら、できる。' },
  ],
  'blade-dancer': [
    // 2026-10-27 下書き＝docs/research/2026-10-27-blade-adv-draft.md（試作で作者が通した台詞と絵のまま）。
    //   上がったままの手は割り込みの寄り (絵を差し替えて 1.38 倍から急に戻す)。 その間だけ劇場の空気を止める。光は画面に足さない。
    //   2択の間は、 上がったままの手を見せたまま選ばせる。
    { kind: 'picture', src: '/adv/blade/fallen_bow.webp', fadeMs: 800 },
    { kind: 'stage', effect: 'zoom', scale: 1.08, x: .5, y: .5, ms: 1 }, { kind: 'stage', effect: 'zoom', scale: 1, x: .5, y: .5, ms: 4200 },
    { kind: 'cue', effect: 'sfx:adv_blade_drop' }, { kind: 'cue', effect: 'amb:adv_blade_theater' },
    { kind: 'narration', text: '踊り手は、一礼の形のまま、倒れた。', pre: 1300 },
    { kind: 'picture', src: '/adv/blade/raised_hand.webp', fadeMs: 0 },
    { kind: 'stage', effect: 'zoom', scale: 1.38, x: .5, y: .5, ms: 1 }, { kind: 'stage', effect: 'zoom', scale: 1, x: .5, y: .5, ms: 150 },
    { kind: 'cue', effect: 'amb:off' },
    { kind: 'narration', text: '腕だけが、拍手を待つように、上がったまま動かなかった。', pre: 250 },
    { kind: 'lunaire', speech: 'spoken', text: '拍手、待ってたの？' },
    { kind: 'picture', src: '/adv/blade/fallen_bow.webp', fadeMs: 0 },
    { kind: 'stage', effect: 'zoom', scale: 1, x: .5, y: .5, ms: 1 },
    { kind: 'cue', effect: 'amb:adv_blade_theater' },
    { kind: 'narration', text: '光が、ほどけて、流れていった。' },
    { kind: 'narration', text: '光の流れる先を、目で追った。' },
    { kind: 'voice', text: '——あなたは、応えてあげる人でしょう。' },
    { kind: 'picture', src: '/adv/blade/raised_hand.webp', fadeMs: 0 },
  ],
  'hollow-actor': [
    { kind: 'narration', text: '空っぽは、ゆっくりと、舞台に、倒れた。' },
    { kind: 'narration', text: '仮面だけが、残った。' },
    { kind: 'narration', text: '空っぽの仮面から、目を、逸らせなかった。' },
    { kind: 'lunaire', text: '……わたしには、ある、から。' },
  ],
  'former-princess': [
    // 2026-10-27 下書き＝docs/research/2026-10-27-princess-adv-draft.md（試作で作者が通した台詞と絵のまま）。
    //   最後まで明るい人として終わる。 光は画面に足さない。 回想 (cue flashback＋beat 5000) と、 この後の5秒の選択は今のまま。
    //   回想は render.ts drawInterlude が activeCueBeat('flashback') を見て public/flashback の4枚を出す (絵の場面でも同じ)。
    //   絵の順=騎士の長槍→覚醒獣の虚無→前の hollow-actor の仮面→先代がほどける姿 (ワークブック選定順)。
    { kind: 'picture', src: '/adv/princess/kneel.webp', fadeMs: 800 },
    { kind: 'stage', effect: 'zoom', scale: 1.08, x: .5, y: .5, ms: 1 }, { kind: 'stage', effect: 'zoom', scale: 1, x: .5, y: .5, ms: 5000 },
    { kind: 'cue', effect: 'amb:adv_princess_stage_air' }, { kind: 'cue', effect: 'sfx:adv_princess_shards' },
    { kind: 'narration', text: '舞台の上に、女が膝をついていた。', pre: 1400 },
    { kind: 'boss', name: '先代', text: '……あーあ。負けちゃった。' },
    { kind: 'cue', effect: 'sfx:adv_princess_tiara' },
    { kind: 'boss', name: '先代', text: '……次の幕は、あなたの番、ね。', pace: 'slow' },
    { kind: 'lunaire', speech: 'spoken', text: '私の番って、何の？' },
    { kind: 'narration', text: '女は、笑っただけだった。' },
    { kind: 'narration', text: '倒れた女から、光が、ほどけていく。' },
    { kind: 'stage', effect: 'zoom', scale: 1.05, x: .5, y: .05, ms: 2000 },
    { kind: 'narration', text: '光は、頬をかすめて、常夜燈へと、昇っていった。' },
    { kind: 'lunaire', speech: 'inner', text: 'ちゃんと、終わらせた……よね？' },
    { kind: 'picture', src: '/adv/princess/black.webp', fadeMs: 400 },
    { kind: 'stage', effect: 'zoom', scale: 1, x: .5, y: .5, ms: 1 },
    { kind: 'cue', effect: 'amb:fade' },
    { kind: 'cue', effect: 'flashback' },
    { kind: 'beat', ms: 5000 },
    { kind: 'narration', text: '声は、まだ、聞こえなかった。' },
    { kind: 'lunaire', speech: 'inner', text: 'だったら、私は、何をすればいい？' },
  ],
}

// 戦闘後の2択 (True=options[0]/+1awaken, Loop=options[1]/+0awaken)。 ワークブック台帳確定文言。
//   nameless-shade (チュートリアル) と former-princess (箱3で特殊実装) は意図的に未定義のまま。
const RAW_INTERLUDE_CHOICES: Record<string, AdvChoice> = {
  'dream-hound': {
    // 2026-10-27 3版: True＝確かめられなかったことを確かめさせる (獣に空の玉座を見せる)・Loop＝元の形へ戻す。
    //   True の終わり「……誰も、いなかったね。」は戦いの前の「誰も、いないよ。」と対。
    options: [
      {
        text: '「獣の頭を、空の玉座へ向ける」', awaken: 1, resultLines: [
          { kind: 'stage', effect: 'zoom', scale: 1.06, x: .5, y: .892, ms: 2600 },
          { kind: 'cue', effect: 'sfx:adv_hound_chain_small' },
          { kind: 'narration', text: '獣の頭を両手で持ち上げ、玉座のほうへ向けた。' },
          { kind: 'picture', src: '/adv/hound/throne_empty.webp', fadeMs: 500 },
          { kind: 'stage', effect: 'zoom', scale: 1, x: .5, y: -.285, ms: 1 }, { kind: 'stage', effect: 'zoom', scale: 1.06, x: .5, y: -.285, ms: 4000 },
          { kind: 'cue', effect: 'amb:off' },
          { kind: 'narration', text: '階段の上の玉座は、空いていた。', pre: 700 },
          { kind: 'cue', effect: 'sfx:adv_hound_chain_small' },
          { kind: 'narration', text: '鎖が、一度だけ鳴った。' },
          { kind: 'lunaire', speech: 'spoken', text: '……誰も、いなかったね。' },
        ],
      },
      {
        text: '「王冠を、玉座へ戻してあげる」', awaken: 0, resultLines: [
          { kind: 'picture', src: '/adv/hound/crown_floor.webp', fadeMs: 0 },
          { kind: 'stage', effect: 'zoom', scale: 1.38, x: .5, y: .5, ms: 1 }, { kind: 'stage', effect: 'zoom', scale: 1, x: .5, y: .5, ms: 150 },
          { kind: 'cue', effect: 'sfx:adv_hound_chain_crown' },
          { kind: 'narration', text: '床に転がった王冠を、拾い上げた。', pre: 200 },
          { kind: 'stage', effect: 'insert', src: '/adv/hound/black.webp', fadeMs: 500 },
          { kind: 'cue', effect: 'amb:fade' },
          { kind: 'beat', ms: 700 },
          { kind: 'cue', effect: 'sfx:adv_hound_crown_place' },
          { kind: 'narration', text: '玉座のうえに、そっと戻す。', pre: 200 },
          { kind: 'lunaire', speech: 'inner', text: '……これで、ちゃんと。' },
        ],
      },
    ],
  },
  'bride': {
    options: [
      {
        text: '「止まった針を、進めてみる」', awaken: 1, resultLines: [
          { kind: 'narration', text: '大時計の針に、指を触れた。' },
          { kind: 'narration', text: '婚礼の刻から、一分、進めた。' },
          { kind: 'lunaire', speech: 'inner', text: 'これで、いい気がする……' },
        ],
      },
      {
        text: '「止まった針を、婚礼の刻に戻してあげる」', awaken: 0, resultLines: [
          { kind: 'narration', text: '動いてしまった針を、婚礼の刻に戻した。' },
          { kind: 'lunaire', text: '……これで、ちゃんと。' },
        ],
      },
    ],
  },
  'vigil-knight': {
    // 2026-10-28 5版: True＝鎖をほどいて扉の奥を見せる (錠は掛かっていなかった・寝台は空)・Loop＝槍を元の形へ戻す。
    //   True の終わりは LUNAIRE の一言を置かず、 騎士の「……そうか。」のあと、 振り返った騎士が光になって消える。
    options: [
      {
        text: '「鎖を、ほどいて見せる」', awaken: 1, resultLines: [
          { kind: 'figure', src: null, fadeMs: 500 },
          { kind: 'picture', src: '/adv/knight/chain_lock.webp', fadeMs: 500 },
          { kind: 'stage', effect: 'zoom', scale: 1, x: .5, y: .267, ms: 1 }, { kind: 'stage', effect: 'zoom', scale: 1.05, x: .5, y: .267, ms: 4000 },
          { kind: 'narration', text: '鎖の錠に、手をかけた。', pre: 700 },
          { kind: 'cue', effect: 'amb:off' },
          { kind: 'narration', text: '錠は、掛かっていなかった。', pre: 700 },
          { kind: 'stage', effect: 'insert', src: '/adv/knight/black.webp', fadeMs: 300 },
          { kind: 'cue', effect: 'sfx:adv_knight_chain_drop' },
          { kind: 'narration', text: '鎖が、床に落ちた。', pre: 1000 },
          { kind: 'cue', effect: 'sfx:adv_knight_door' },
          { kind: 'narration', text: '扉を、開けた。', pre: 900 },
          // 黒を地の絵へ移してから被せを外し、 空の寝台を1秒かけて出す。
          { kind: 'picture', src: '/adv/knight/black.webp', fadeMs: 0 },
          { kind: 'stage', effect: 'insert', src: null },
          { kind: 'picture', src: '/adv/knight/bed_empty.webp', fadeMs: 1000 },
          { kind: 'stage', effect: 'zoom', scale: 1, x: .5, y: .35, ms: 1 }, { kind: 'stage', effect: 'zoom', scale: 1.08, x: .5, y: .35, ms: 5000 },
          { kind: 'narration', text: '奥に、寝台がひとつあった。', pre: 1300 },
          { kind: 'narration', text: '誰も、寝ていなかった。' },
          { kind: 'boss', name: '騎士', text: '……そうか。', pre: 1300 },
          // 10-29 作者「会話がそのままブツッと終わる…死んでない感じする」: 鎧の崩れる音と暗転をやめ、 振り返った騎士が光になって消える (末尾は VANISH)。
          { kind: 'picture', src: '/adv/knight/lance_fallen.webp', fadeMs: 1200 },
          { kind: 'stage', effect: 'zoom', scale: 1.05, x: .5, y: .5, ms: 1 }, { kind: 'stage', effect: 'zoom', scale: 1, x: .5, y: .5, ms: 3000 },
          { kind: 'cue', effect: 'sfx:exec_unique_knight_settle' },
          { kind: 'narration', text: '振り返ると、騎士は、膝をついたままだった。', pre: 1000 },
        ],
      },
      {
        // 10-29 作者「中身…がもうなくなっちゃってるけど、側の甲冑だけ残ってて、武器も残ってて…中性(忠誠)の意を示す形に戻してあげてる」:
        //   槍へ手を伸ばす所で騎士が光になって先に消え、 残った空の甲冑と槍を LUNAIRE が整える (続きは KNIGHT_LOOP_AFTER)。
        text: '「槍を、扉の前に立ててあげる」', awaken: 0, resultLines: [
          { kind: 'narration', text: '落ちていた槍に、手を伸ばした。' },
        ],
      },
    ],
  },
  'the-executioner': {
    // 2026-10-27 版4: True＝主の印が無い所を男に見せる (刃の腕が床に下りる)・Loop＝空いた寝台に寝かせる。
    //   True の終わり「主の判決じゃ、なかったね。」は戦いの前の介錯「主の判決である。」と対。
    options: [
      {
        text: '「印の無い所を、男に見せる」', awaken: 1, resultLines: [
          { kind: 'stage', effect: 'zoom', scale: 1.06, x: .5, y: .696, ms: 2600 },
          { kind: 'cue', effect: 'sfx:adv_exec_paper_open' },
          { kind: 'narration', text: '判決書を拾い、覆面の前に広げた。' },
          { kind: 'picture', src: '/adv/executioner/verdict_seals.webp', fadeMs: 0 },
          { kind: 'stage', effect: 'zoom', scale: 1.38, x: .275, y: .433, ms: 1 }, { kind: 'stage', effect: 'zoom', scale: 1.2, x: .275, y: .433, ms: 150 },
          { kind: 'narration', text: '最後の名前の横には、印が無かった。', pre: 250 },
          { kind: 'picture', src: '/adv/executioner/fallen_floor.webp', fadeMs: 0 },
          { kind: 'stage', effect: 'zoom', scale: 1.06, x: .5, y: .696, ms: 1 },
          { kind: 'cue', effect: 'sfx:adv_exec_blade_floor' },
          { kind: 'narration', text: '刃の腕が、ゆっくりと、床に下りた。', pre: 500 },
          { kind: 'cue', effect: 'amb:off' },
          { kind: 'beat', ms: 400 },
          { kind: 'lunaire', speech: 'spoken', text: '主の判決じゃ、なかったね。' },
        ],
      },
      {
        text: '「空いた寝台に寝かせ、布を掛けてあげる」', awaken: 0, resultLines: [
          { kind: 'picture', src: '/adv/executioner/pic7.webp', fadeMs: 500 },
          { kind: 'stage', effect: 'zoom', scale: 1, x: .5, y: .5, ms: 1 },
          { kind: 'narration', text: '重い体を抱えて、空いた寝台へ寝かせた。' },
          { kind: 'cue', effect: 'sfx:adv_cloth_pull' },
          { kind: 'narration', text: '白い布を、頭まで掛けた。端を揃えて、皺を伸ばした。' },
          { kind: 'lunaire', speech: 'inner', text: '……これで、ちゃんと。' },
        ],
      },
    ],
  },
  'crowned-king-awoken': {
    // 2026-10-28 作者が書き直し案から T1・L1 を選んだ (docs/research/2026-10-28-awoken-choice-redraft.md)。
    //   True＝石をどけて玉座を掘り出し、 獣に「主はいない」と自分で確かめさせる。 声は黙る。
    //   Loop＝「ずっと、守ってたんだね」と守ってきた事だけを認め、 獣はまた守りに戻る。
    options: [
      {
        text: '「埋もれた玉座の石を、どける」', awaken: 1, resultLines: [
          { kind: 'stage', effect: 'zoom', scale: 1.3, x: .53, y: .26, ms: 2600 },
          { kind: 'narration', text: '石の山に、手をかけた。' },
          { kind: 'cue', effect: 'sfx:adv_awoken_drag' },
          { kind: 'narration', text: '石を、ひとつずつ、下ろした。', pre: 300 },
          { kind: 'picture', src: '/adv/awoken/throne_unearthed.webp', fadeMs: 900 },
          { kind: 'stage', effect: 'zoom', scale: 1, x: .5, y: .5, ms: 1 },
          { kind: 'cue', effect: 'sfx:adv_awoken_crouch' },
          { kind: 'narration', text: '獣が、顔を上げた。', pre: 700 },
          { kind: 'narration', text: '石の下から、玉座が出てきた。' },
          { kind: 'narration', text: '座る所には、砂が積もっていた。' },
          { kind: 'lunaire', speech: 'spoken', text: 'ここなら、届く？', pre: 500 },
          { kind: 'picture', src: '/adv/awoken/beast_nose_seat.webp', fadeMs: 900 },
          { kind: 'cue', effect: 'sfx:adv_awoken_claw_scrape' },
          { kind: 'narration', text: '獣が、座る所へ、鼻先を寄せた。', pre: 600 },
          { kind: 'cue', effect: 'sfx:awoken_voice_haunt_huff' },
          { kind: 'boss', name: '獣', text: '主……？', pre: 400 },
          { kind: 'cue', effect: 'amb:off' },
          { kind: 'narration', text: '獣は、しばらく、動かなかった。', pre: 1200 },
          { kind: 'narration', text: '声は、何も言わなかった。', pre: 800 },
          { kind: 'beat', ms: 1200 },
        ],
      },
      {
        text: '「『ずっと、守ってたんだね』と言ってあげる」', awaken: 0, resultLines: [
          { kind: 'narration', text: '獣の頭に、手を置いた。' },
          { kind: 'lunaire', speech: 'spoken', text: 'ずっと、守ってたんだね。', pre: 400 },
          { kind: 'cue', effect: 'sfx:awoken_voice_haunt_huff' },
          { kind: 'narration', text: '獣の息が、ゆっくりになった。', pre: 500 },
          { kind: 'cue', effect: 'sfx:awoken_voice_haunt_growl_s' },
          { kind: 'boss', name: '獣', text: '今度こそ。……守る。', pre: 300 },
          { kind: 'stage', effect: 'insert', src: '/adv/awoken/black.webp', fadeMs: 1200 },
          { kind: 'cue', effect: 'amb:fade' },
          { kind: 'lunaire', speech: 'inner', text: '……これで、ちゃんと。' },
        ],
      },
    ],
  },
  'inverted-jester': {
    // 2026-10-27 5版: True＝綱の結び目を一緒に見て、 数え間違えていなかったと確かめさせる (それでも東の空は暗い)・Loop＝綱を巻き直す。
    //   True の終わり「間違えてなかったね。」は戦いの前の「鳴いたから、朝が来ないの？」への答え。 鶏は鳴かせない。
    options: [
      {
        text: '「綱の結び目を、一緒に見る」', awaken: 1, resultLines: [
          { kind: 'lunaire', speech: 'spoken', text: '綱、ほどくね。' },
          { kind: 'cue', effect: 'sfx:adv_jester_rope_rub' },
          { kind: 'narration', text: '獣の腰から、綱をほどいた。' },
          { kind: 'picture', src: '/adv/jester/knots.webp', fadeMs: 0 },
          { kind: 'stage', effect: 'zoom', scale: 1.38, x: .5, y: .5, ms: 1 }, { kind: 'stage', effect: 'zoom', scale: 1, x: .5, y: .5, ms: 150 },
          { kind: 'narration', text: '結び目を、ひとつずつ、指でたどった。', pre: 200 },
          { kind: 'narration', text: '一番新しい結び目も、ちゃんと結んであった。' },
          { kind: 'picture', src: '/adv/jester/fallen.webp', fadeMs: 0 },
          { kind: 'boss', name: '鶏', text: '……結んで、ある……？' },
          { kind: 'boss', name: '鶏', text: '数えきってから……鳴いた、の……？' },
          { kind: 'picture', src: '/adv/jester/east_sky.webp', fadeMs: 500 },
          { kind: 'cue', effect: 'amb:off' },
          { kind: 'narration', text: '東の空は、それでも、暗いままだった。', pre: 700 },
          { kind: 'lunaire', speech: 'spoken', text: '間違えてなかったね。' },
        ],
      },
      {
        text: '「綱を、元どおり巻き直してあげる」', awaken: 0, resultLines: [
          { kind: 'cue', effect: 'sfx:adv_jester_rope_creak' },
          { kind: 'narration', text: 'ほどけた綱を、獣の腰に巻き直した。' },
          { kind: 'narration', text: '獣は、また最初から数え始めた。' },
          { kind: 'boss', name: '鶏', text: '……ひとつ。……ふたつ。……みっつ。' },
          { kind: 'lunaire', speech: 'spoken', text: 'はい、巻けたよ。数えてて。' },
          { kind: 'narration', text: '数は、どこまでも続いていた。' },
          { kind: 'stage', effect: 'insert', src: '/adv/jester/black.webp', fadeMs: 600 },
          { kind: 'cue', effect: 'amb:fade' },
          { kind: 'lunaire', speech: 'inner', text: '……これで、ちゃんと……', pre: 900 },
        ],
      },
    ],
  },
  'blade-dancer': {
    // 2026-10-27: True＝空いた客席へ呼びかけ、 誰もいないことを確かめさせる (作者が推奨を選んだ)・Loop＝拍手を返してあげる。
    //   True は声のこだまのあと劇場の空気も消して無音にし、 上がっていた指が閉じる。 Loop は拍手のあと深い礼で暗転。
    options: [
      {
        text: '「空いた客席へ、呼びかける」', awaken: 1, resultLines: [
          { kind: 'picture', src: '/adv/blade/empty_seats.webp', fadeMs: 500 },
          { kind: 'stage', effect: 'zoom', scale: 1, x: .5, y: .5, ms: 1 },
          { kind: 'narration', text: '客席へ向かって、声を上げた。', pre: 600 },
          { kind: 'stage', effect: 'zoom', scale: 1.06, x: .5, y: .5, ms: 1 }, { kind: 'stage', effect: 'zoom', scale: 1, x: .5, y: .5, ms: 4200 },
          { kind: 'cue', effect: 'sfx:adv_blade_echo' },
          { kind: 'narration', text: '声は、空いた席のあいだを渡って、遠くから返ってきた。' },
          { kind: 'cue', effect: 'amb:off' },
          { kind: 'narration', text: 'ほかに、返る音はなかった。' },
          { kind: 'picture', src: '/adv/blade/hand_closed.webp', fadeMs: 0 },
          { kind: 'stage', effect: 'zoom', scale: 1.38, x: .5, y: .5, ms: 1 }, { kind: 'stage', effect: 'zoom', scale: 1, x: .5, y: .5, ms: 150 },
          { kind: 'narration', text: '上がっていた指が、ゆっくり、閉じた。', pre: 900 },
        ],
      },
      {
        text: '「両手を、強く打ち合わせて、拍手を返してあげる」', awaken: 0, resultLines: [
          { kind: 'picture', src: '/adv/blade/empty_seats.webp', fadeMs: 500 },
          { kind: 'stage', effect: 'zoom', scale: 1, x: .5, y: .5, ms: 1 }, { kind: 'stage', effect: 'zoom', scale: 1.04, x: .5, y: .5, ms: 2400 },
          { kind: 'cue', effect: 'sfx:adv_blade_step' },
          { kind: 'narration', text: '客席の中央へ、進んだ。', pre: 600 },
          { kind: 'cue', effect: 'sfx:adv_blade_clap' }, { kind: 'stage', effect: 'shake', power: 4, ms: 260 },
          { kind: 'narration', text: '両手を、強く、打ち合わせた。' },
          { kind: 'stage', effect: 'zoom', scale: 1.1, x: .5, y: .5, ms: 4000 },
          { kind: 'narration', text: '拍手は、空いた客席に、響いた。' },
          { kind: 'picture', src: '/adv/blade/deep_bow.webp', fadeMs: 500 },
          { kind: 'stage', effect: 'zoom', scale: 1, x: .5, y: .5, ms: 1 },
          { kind: 'narration', text: '上がっていた腕が、礼の形に、深く下りた。', pre: 500 },
          { kind: 'stage', effect: 'insert', src: '/adv/blade/black.webp', fadeMs: 900 },
          { kind: 'cue', effect: 'amb:fade' },
          { kind: 'lunaire', speech: 'inner', text: '……これで、ちゃんと。', pre: 300 },
        ],
      },
    ],
  },
  'hollow-actor': {
    options: [
      {
        text: '「……お疲れさま。」と呟いて、仮面を、見送る形で、進む', awaken: 1, resultLines: [
          { kind: 'lunaire', text: '……お疲れさま。' },
          { kind: 'narration', text: '呟いて、仮面を、見送る形で、進んだ。' },
        ],
      },
      {
        text: '仮面を、拾って、空っぽの顔に、戻してあげる', awaken: 0, resultLines: [
          { kind: 'narration', text: '仮面を、拾った。そして、空っぽの顔に、戻してあげた。' },
        ],
      },
    ],
  },
}

// 2択の結果の終わりに、 倒れたボスが光になって消える (2026-10-29 作者「会話の最後に消えていって、光になって…普通に会話してたから死んでない感じする」)。
//   どの部屋も True・Loop の両方。 黒の被せを外してボスの絵へ戻し、 ボスの体から光がほどけて、 画面は黒で終わる。 光の行き先は見せない・言わない。
//   案 A＝淡い銀の粒がその場で薄れる・案 B＝青白い粒が上へ流れる。 既定は B (10-29 作者「こっちがいいです」)。 開発版の URL advFade=a で前の案 A。 中心と広さは絵に対する割合。
type Vanish = { src: string; x: number; y: number; rx: number; ry: number }
const VANISH: Record<string, [Vanish, Vanish]> = {
  'dream-hound': [
    { src: '/adv/hound/beast_fallen.webp', x: .45, y: .48, rx: .5, ry: .25 },
    { src: '/adv/hound/beast_fallen.webp', x: .45, y: .48, rx: .5, ry: .25 },
  ],
  'bride': [
    { src: '/adv/bride/hall_shifted.webp', x: .5, y: .95, rx: .45, ry: .35 },
    { src: '/adv/bride/hall_shifted.webp', x: .5, y: .95, rx: .45, ry: .35 },
  ],
  'vigil-knight': [
    { src: '/adv/knight/lance_fallen.webp', x: .85, y: .35, rx: .25, ry: .5 },
    { src: '/adv/knight/lance_fallen.webp', x: .85, y: .35, rx: .25, ry: .5 },
  ],
  'the-executioner': [
    { src: '/adv/executioner/fallen_floor.webp', x: .45, y: .55, rx: .35, ry: .2 },
    { src: '/adv/executioner/pic7.webp', x: .55, y: .55, rx: .2, ry: .15 },
  ],
  'crowned-king-awoken': [
    { src: '/adv/awoken/beast_nose_seat.webp', x: .7, y: .4, rx: .35, ry: .35 },
    { src: '/adv/awoken/beast_rest.webp', x: .45, y: .5, rx: .45, ry: .2 },
  ],
  'inverted-jester': [
    { src: '/adv/jester/fallen.webp', x: .55, y: .62, rx: .45, ry: .25 },
    { src: '/adv/jester/fallen.webp', x: .55, y: .62, rx: .45, ry: .25 },
  ],
  'blade-dancer': [
    { src: '/adv/blade/hand_closed.webp', x: .55, y: .65, rx: .3, ry: .45 },
    { src: '/adv/blade/deep_bow.webp', x: .5, y: .5, rx: .3, ry: .45 },
  ],
}
// 騎士の Loop の続き (2026-10-29 作者・77回目「全部扉とかの前に整えておいてあげて…返事がないから、もう死んじゃったんだなってことを確認した上で…扉の前に置いてあげる」)。
//   光が昇りきったあと、 空の甲冑に呼びかけて返事がないのを確かめ、 鎧・兜・槍を全部、 鎖の扉の前へ運んで整える。 兜だけを置く形は作者が避けた。 光の演出は足さず、 整えた絵のまま黒へ閉じる。
const KNIGHT_LOOP_AFTER: AdvLine[] = [
  { kind: 'narration', text: '光は、ひとつ残らず、上へ昇っていった。', pre: 900 },
  // 消えきった黒のまま絵を差し替え (消えた跡の黒は picture で外れる)、 空の甲冑をゆっくり出す。
  { kind: 'picture', src: '/adv/knight/black.webp', fadeMs: 600 },
  { kind: 'picture', src: '/adv/knight/lance_fallen.webp', fadeMs: 1600 },
  { kind: 'stage', effect: 'zoom', scale: 1, x: .5, y: .5, ms: 1 }, { kind: 'stage', effect: 'zoom', scale: 1.04, x: .5, y: .5, ms: 5000 },
  { kind: 'narration', text: '鎧だけが、残っていた。', pre: 1600 },
  { kind: 'lunaire', speech: 'spoken', text: 'ねえ、聞こえる？' },
  { kind: 'beat', ms: 1400 },
  { kind: 'narration', text: '返事は、なかった。' },
  { kind: 'narration', text: '籠手に、触れた。' },
  { kind: 'narration', text: '中は、空っぽだった。' },
  { kind: 'lunaire', speech: 'inner', text: '……もう、いないんだ……' },
  { kind: 'narration', text: '槍を、拾い上げた。', pre: 600 },
  { kind: 'picture', src: '/adv/knight/black.webp', fadeMs: 700 },
  { kind: 'picture', src: '/adv/knight/loop_restore_b.webp', fadeMs: 1400 },
  { kind: 'stage', effect: 'zoom', scale: 1.06, x: .5, y: .45, ms: 1 }, { kind: 'stage', effect: 'zoom', scale: 1, x: .5, y: .45, ms: 6000 },
  { kind: 'narration', text: '鎧を、扉の前へ運んだ。', pre: 900 },
  { kind: 'narration', text: '扉のほうへ向けて、膝をつかせた。' },
  { kind: 'narration', text: '兜を、その横に置いた。' },
  { kind: 'narration', text: '槍を、扉の前に立てた。' },
  { kind: 'cue', effect: 'amb:fade' },
  { kind: 'lunaire', speech: 'spoken', text: 'これで、扉を守れるね。', pre: 900 },
  { kind: 'beat', ms: 1600 },
  { kind: 'picture', src: '/adv/knight/black.webp', fadeMs: 2400 },
  { kind: 'beat', ms: 2800 },
]
let advFadeVariant: 'a' | 'b' = 'b'
export function setAdvFadeVariant(v: string | null): void { advFadeVariant = v === 'a' ? 'a' : 'b' }
function advVanishLines(v: Vanish, before: AdvLine[]): AdvLine[] {
  let covered = false, shown: string | null = null
  for (const line of before) {
    if (line.kind === 'stage' && line.effect === 'insert') covered = !!line.src
    if (line.kind === 'picture') shown = line.src
  }
  const b = advFadeVariant === 'b'
  const uncover: AdvLine[] = covered ? [{ kind: 'picture', src: v.src.replace(/[^/]+$/, 'black.webp'), fadeMs: 0 }, { kind: 'stage', effect: 'insert', src: null }] : []
  // 今と同じ絵なら出し直さない。
  const show: AdvLine[] = covered || shown !== v.src ? [{ kind: 'picture', src: v.src, fadeMs: covered ? 1400 : 900 }] : []
  return [
    // 黒の被せの下の絵を黒にしてから被せを外し、 ボスの絵をゆっくり出す。
    ...uncover,
    { kind: 'figure', src: null, fadeMs: 500 },
    ...show,
    { kind: 'stage', effect: 'zoom', scale: 1, x: .5, y: .5, ms: 1200 },
    { kind: 'beat', ms: covered ? 1800 : 1300 },
    { kind: 'stage', effect: 'dissolve', x: v.x, y: v.y, rx: v.rx, ry: v.ry, tint: b ? 'blue' : 'silver', rise: b },
    // 消えきる (ADV_DISSOLVE_MS) まで待ってから終わる。 終わると時計が止まるため。
    { kind: 'beat', ms: 4600 },
  ]
}

// 軍配者たち (hollow-actor) の書き直し版 (2026-10-30・docs/research/2026-10-30-hollow-actor-adv-rewrite.md)。 開発版の URL twinsAdv=new だけで出す。
//   絵は下の一枚絵 8枚 (twinsAdvArt)。 音は音の専用セッションが作るまで足さない。
//   決まり＝台帳 story 45・46回目 (作戦の間で負けない手を読み続け、 選べないまま城が落ちた／2択＝駒を始めの並びに戻す・駒を一つ自分で置く)。
let twinsAdvDraft = false
export function setTwinsAdvDraft(on: boolean): void { twinsAdvDraft = on }
const TWINS_ROOM = '/bg/twins-room.png'
const TWINS_PREBATTLE: AdvLine[] = [
  { kind: 'picture', src: TWINS_ROOM, fadeMs: 1000 },
  { kind: 'stage', effect: 'zoom', scale: 1.06, x: .5, y: .7, ms: 1 }, { kind: 'stage', effect: 'zoom', scale: 1, x: .5, y: .7, ms: 5000 },
  { kind: 'narration', text: '部屋の真ん中に、大きな盤が据えてあった。', pre: 1200 },
  { kind: 'narration', text: '盤には、この城の地図が描かれていた。' },
  { kind: 'narration', text: '頭巾をかぶった二人が、背中合わせに駒を動かしていた。' },
  // 姉＝敵の駒を読む (家臣の丁寧な口調)・妹＝味方の駒を読む (少し幼い)。 台帳 story.md 45回目。
  { kind: 'boss', name: '姉', text: '敵は、東の門から参ります。' },
  { kind: 'boss', name: '妹', text: 'なら、橋の手前で止めます。' },
  { kind: 'boss', name: '姉', text: '……それでは、西の塀が空きます。' },
  { kind: 'boss', name: '妹', text: '……じゃあ、西へ回す。' },
  { kind: 'boss', name: '姉', text: 'すると、東が抜けます。' },
  { kind: 'narration', text: '同じ駒が、同じ所を、行ったり来たりしていた。' },
  { kind: 'boss', name: '妹', text: 'もう一手。もう一手、先まで読めば……' },
  { kind: 'boss', name: '姉', text: 'ええ。抜け道のない手が、きっとある。' },
  { kind: 'boss', name: '姉', text: '主さま、もう少しだけ、お待ちください。' },
  { kind: 'narration', text: '部屋のどこにも、主の姿は無かった。', pre: 500 },
  { kind: 'voice', text: '——ずいぶん、待たせてるのね。', pace: 'quick' },
  { kind: 'lunaire', speech: 'spoken', text: 'ねえ。' },
  { kind: 'narration', text: '二人の手が、止まった。', pre: 500 },
  { kind: 'boss', name: '姉', text: '……駒が、ひとつ、増えました。' },
  { kind: 'boss', name: '妹', text: '敵の駒？ 味方の駒？' },
  { kind: 'boss', name: '姉', text: 'わかりません。だから、読みます。' },
  { kind: 'boss', name: '妹', text: 'この駒は、次に……こっちへ来る。' },
  { kind: 'voice', text: '——あら。あなたまで、駒にされちゃった。', pace: 'quick' },
  { kind: 'lunaire', speech: 'inner', text: '……ずっと、二人で、あの夜を並べ直していた……' },
  { kind: 'voice', text: '——そう。あなたが、手を止めてあげられる。' },
  { kind: 'lunaire', speech: 'spoken', text: 'その読み、私が止めてあげる。' },
  { kind: 'boss', name: '妹', text: 'なら、先に打つ。' },
  { kind: 'boss', name: '姉', text: 'ええ。読んだとおりに。' },
  { kind: 'stage', effect: 'zoom', scale: 1.2, x: .5, y: .6, ms: 140 }, { kind: 'stage', effect: 'flash' },
  { kind: 'cue', effect: 'blackout' },
  { kind: 'beat', ms: 300 },
]
// 会話場面の一枚絵 8枚 (2026-11-01・作者が型と残り7枚の置き場を通した・docs/research/2026-11-01-twins-adv-art.md)。 配布版の既定。 開発版の URL twinsAdvArt=off で城の一室の背景だけに戻す。
let twinsAdvArt = true
export function setTwinsAdvArt(on: boolean): void { twinsAdvArt = on }
const TWINS_FALLEN = '/adv/twins/fallen_board.webp'
// 最初の部屋の背景を差し替える絵 (戦いの前＝盤の部屋・戦いの後＝倒れた二人) と、 この文の前 (before)・後 (after) に出す絵。
const TWINS_ART_FIRST = { pre: '/adv/twins/board_room.webp', inter: TWINS_FALLEN }
const TWINS_ART_AT: [text: string, src: string, at: 'before' | 'after'][] = [
  ['頭巾をかぶった二人が、背中合わせに駒を動かしていた。', '/adv/twins/reading_back.webp', 'after'],
  ['部屋のどこにも、主の姿は無かった。', '/adv/twins/empty_seat.webp', 'before'],
  ['二人の手が、止まった。', '/adv/twins/hands_stop.webp', 'before'],
  ['盤の上に、駒がひとつだけ残っていた。', '/adv/twins/last_piece.webp', 'before'],
  ['東の門の前に、置いた。', '/adv/twins/piece_gate.webp', 'before'],
  ['地図の上に、あの夜の始めの並びをつくった。', '/adv/twins/start_rows.webp', 'before'],
]
function withTwinsArt(lines: AdvLine[], first?: string): AdvLine[] {
  if (!twinsAdvArt) return lines
  let swapped = false
  return lines.flatMap((l): AdvLine[] => {
    if (first && !swapped && l.kind === 'picture' && l.src === TWINS_ROOM) { swapped = true; return [{ ...l, src: first }] }
    const hit = 'text' in l ? TWINS_ART_AT.find(a => a[0] === l.text) : undefined
    if (!hit) return [l]
    const pic: AdvLine = { kind: 'picture', src: hit[1], fadeMs: 600 }
    return hit[2] === 'before' ? [pic, l] : [l, pic]
  })
}
const TWINS_INTERLUDE_ROOM: AdvLine[] = [
  { kind: 'picture', src: TWINS_ROOM, fadeMs: 800 },
  { kind: 'stage', effect: 'zoom', scale: 1.08, x: .5, y: .7, ms: 1 }, { kind: 'stage', effect: 'zoom', scale: 1, x: .5, y: .7, ms: 4200 },
  { kind: 'narration', text: '盤は、傾いたまま止まっていた。', pre: 1300 },
  { kind: 'narration', text: '二人は、盤の脇に並んで倒れていた。' },
  { kind: 'narration', text: '駒が、床じゅうに散らばっていた。' },
  { kind: 'boss', name: '姉', text: '……読み、切れません、でした。' },
  { kind: 'boss', name: '妹', text: 'どこに置いても……抜け道が、残るの。' },
  { kind: 'boss', name: '姉', text: 'あの夜も、そうでした。' },
  { kind: 'boss', name: '妹', text: '並べ直して、並べ直して……' },
  { kind: 'boss', name: '姉', text: '主さまに、何も、申し上げられないまま。' },
  { kind: 'narration', text: '光が、ほどけて、どこかへ流れていった。' },
  { kind: 'lunaire', speech: 'spoken', text: 'ずっと、二人で考えてたんだね。' },
  { kind: 'boss', name: '妹', text: '……あなたは、迷わないの？' },
  // 迷いは見た事実で言う (作者の例＝みんなを終わらせると決めた時・覚醒獣があの時の獣かと思った時)。
  { kind: 'lunaire', speech: 'inner', text: '迷ったこと……', pre: 500 },
  { kind: 'lunaire', speech: 'inner', text: 'みんなを終わらせてあげるって、決めた時' },
  { kind: 'lunaire', speech: 'inner', text: '王冠の獣を、あの時の獣かと思った時も……' },
  { kind: 'lunaire', speech: 'spoken', text: '迷ったよ。でも、ここまで来た。' },
  { kind: 'voice', text: '——そうね。迷いながらでも、歩いてきたもの。' },
  { kind: 'boss', name: '姉', text: '……主さまは、どの手を、望まれたのでしょう。' },
  { kind: 'narration', text: '盤の上に、駒がひとつだけ残っていた。' },
]
const twinsInterlude = (): AdvLine[] => withTwinsArt(TWINS_INTERLUDE_ROOM, TWINS_ART_FIRST.inter)
const TWINS_CHOICE: AdvChoice = {
  // True＝駒を一つ自分で置き、 なぜ置いたかを言う・Loop＝始めの並びに戻し、 答えられない LUNAIRE に二人が「あなたにも分からない」と返して、 また最初から読み始める。
  options: [
    {
      text: '「駒を一つ、自分の手で置いてみる」', awaken: 1, resultLines: [
        { kind: 'narration', text: '残っていた駒を、つまみ上げた。' },
        { kind: 'narration', text: '地図の上で、手が止まった。', pre: 600 },
        { kind: 'lunaire', speech: 'inner', text: 'どこに置いても、抜け道は残る……' },
        { kind: 'narration', text: '東の門の前に、置いた。', pre: 900 },
        { kind: 'boss', name: '姉', text: '……そこは、抜けられます。' },
        { kind: 'boss', name: '妹', text: '……どうして、置いたの？' },
        { kind: 'lunaire', speech: 'spoken', text: '抜け道は、残るかもしれない。' },
        { kind: 'lunaire', speech: 'spoken', text: 'でも、置かなきゃ、何も伝わらないから。' },
        { kind: 'lunaire', speech: 'spoken', text: '「こうしてください」って。' },
        { kind: 'narration', text: '二人が、盤の上の駒を見つめていた。', pre: 600 },
        { kind: 'boss', name: '姉', text: '……主さま。' },
        { kind: 'boss', name: '妹', text: '……こうして、ください。' },
      ],
    },
    {
      text: '「散らばった駒を、あの夜の始めの並びに戻してあげる」', awaken: 0, resultLines: [
        { kind: 'narration', text: '床の駒を、ひとつずつ拾った。' },
        { kind: 'narration', text: '地図の上に、あの夜の始めの並びをつくった。' },
        { kind: 'boss', name: '姉', text: '……始めの、並び。' },
        { kind: 'boss', name: '妹', text: 'ここから、どう打つの？' },
        { kind: 'narration', text: '答えられなかった。', pre: 800 },
        { kind: 'lunaire', speech: 'spoken', text: '……わからない。' },
        { kind: 'boss', name: '姉', text: '……やっぱり。' },
        { kind: 'boss', name: '妹', text: 'あなたにも、わからないんだね。' },
        { kind: 'boss', name: '姉', text: 'では、また最初から。' },
        { kind: 'boss', name: '姉', text: '……敵は、東の門から参ります。', pre: 600 },
        { kind: 'boss', name: '妹', text: '……なら、橋の手前で止めます。' },
        { kind: 'lunaire', speech: 'inner', text: '……これで、ちゃんと……', pre: 600 },
      ],
    },
  ],
}
const twinsChoice = (): AdvChoice => ({ ...TWINS_CHOICE, options: TWINS_CHOICE.options.map(o => ({ ...o, resultLines: withTwinsArt(o.resultLines ?? []) })) })
const TWINS_VANISH_ROOM: [Vanish, Vanish] = [
  { src: TWINS_ROOM, x: .5, y: .78, rx: .35, ry: .15 },
  { src: TWINS_ROOM, x: .5, y: .78, rx: .35, ry: .15 },
]
const TWINS_VANISH_FALLEN: Vanish = { src: TWINS_FALLEN, x: .7, y: .56, rx: .25, ry: .28 }
const twinsVanish = (): [Vanish, Vanish] => twinsAdvArt ? [TWINS_VANISH_FALLEN, TWINS_VANISH_FALLEN] : TWINS_VANISH_ROOM

const FALLBACK: AdvScene = { id: 'interlude:__fallback__', lines: [{ kind: 'voice', text: '幕が変わる。' }] }

export function getInterludeScene(bossId: string): AdvScene { return applyAdvWordingFix(applyAdvVoiceV2(getInterludeSceneRaw(bossId))) }
function getInterludeSceneRaw(bossId: string): AdvScene {
  const twins = bossId === 'hollow-actor' && twinsAdvDraft
  const lines = twins ? twinsInterlude() : RAW_INTERLUDE[bossId]
  if (!lines) return { ...FALLBACK, id: `interlude:${bossId}` }
  const raw = twins ? twinsChoice() : RAW_INTERLUDE_CHOICES[bossId], vanish = twins ? twinsVanish() : VANISH[bossId]
  const choice = raw && vanish
    ? { ...raw, options: raw.options.map((o, i) => {
        const loop = bossId === 'vigil-knight' && i === 1
        const before = o.resultLines ?? []
        return { ...o, resultLines: [...before, ...advVanishLines(vanish[i]!, before), ...(loop ? KNIGHT_LOOP_AFTER : [])] }
      }) }
    : raw
  return choice ? { id: `interlude:${bossId}`, lines, choice } : { id: `interlude:${bossId}`, lines }
}

// 戦闘前 ADV (箱1: 2026-07-06 プラミング)。 ボスに戦闘前シーンが無ければ null
//   (interlude と違いフォールバックしない=段階的に workbook から移植していく前提)。
//   AdvLine を直接書く (toLines の「声/LUNAIRE」自動判定は使わない=narration 等の新種別を明示するため)。

// 覚醒獣の戦う前 (2026-10-28 下書き5版＝docs/research/2026-10-27-awoken-adv-draft.md)。
//   戴冠獣の広間の天井が落ちた跡。 獣は「別の獣」と声に言われ、 戴冠獣と比べる行で違いを見せる。 顔は描かない。
const AWOKEN_PREBATTLE_HEAD: AdvLine[] = [
  { kind: 'picture', src: '/adv/awoken/hall_fallen.webp', fadeMs: 1200 },
  { kind: 'stage', effect: 'zoom', scale: 1, x: .5, y: .107, ms: 1 }, { kind: 'stage', effect: 'zoom', scale: 1.06, x: .5, y: .107, ms: 6000 },
  { kind: 'cue', effect: 'amb:adv_awoken_hall_air' },
  { kind: 'narration', text: '広間の天井が、落ちていた。', pre: 1300 },
  { kind: 'narration', text: '割れた天井から、暗い空が見えていた。' },
  { kind: 'narration', text: '階段の上は、崩れた石に埋まっていた。' },
  { kind: 'voice', text: '——今度は、座る所もないね。', pace: 'quick' },
  { kind: 'picture', src: '/adv/awoken/beast_stand.webp', fadeMs: 1200 },
  { kind: 'narration', text: '石の山の前に、黒い獣が立っていた。', pre: 1300 },
  { kind: 'stage', effect: 'zoom', scale: 1.05, x: .5, y: 1.2, ms: 2600 },
  { kind: 'narration', text: '割れた王冠が、顎に食いこんでいた。' },
]
const AWOKEN_PREBATTLE_CROWN_LINE: Record<'true' | 'loop', AdvLine> = {
  // Loop＝王冠を玉座へ戻した人。 True＝獣の頭の王冠を見た人。
  loop: { kind: 'narration', text: '玉座へ置いてきた王冠に、似ていた。' },
  true: { kind: 'narration', text: 'あの獣の頭にあった王冠に、似ていた。' },
}
const AWOKEN_PREBATTLE_TAIL: AdvLine[] = [
  { kind: 'cue', effect: 'sfx:adv_awoken_chain' },
  { kind: 'narration', text: '脚には、ちぎれた鎖が巻きついていた。' },
  { kind: 'lunaire', speech: 'spoken', text: '……あの時の、獣？' },
  { kind: 'voice', text: '——違うわ。別の獣。', pace: 'quick', pre: 300 },
  { kind: 'narration', text: 'あの獣より、ずっと大きかった。' },
  { kind: 'narration', text: 'あの獣は、玉座の前で伏せていた。' },
  { kind: 'narration', text: 'この獣は、立ったまま、王冠をこちらへ向けた。' },
  { kind: 'picture', src: '/adv/awoken/crown_jaw.webp', fadeMs: 0 },
  { kind: 'stage', effect: 'zoom', scale: 1.38, x: .5, y: .5, ms: 1 }, { kind: 'stage', effect: 'zoom', scale: 1, x: .5, y: .5, ms: 150 },
  { kind: 'cue', effect: 'sfx:awoken_voice_haunt_growl_s' }, { kind: 'stage', effect: 'shake', power: 8, ms: 320 },
  { kind: 'beat', ms: 900 },
  { kind: 'picture', src: '/adv/awoken/beast_stand.webp', fadeMs: 0 },
  { kind: 'stage', effect: 'zoom', scale: 1.05, x: .5, y: 1.2, ms: 1 },
  { kind: 'lunaire', speech: 'spoken', text: 'ねえ。何か、言って。' },
  { kind: 'cue', effect: 'sfx:awoken_voice_haunt_growl_l' }, { kind: 'stage', effect: 'shake', power: 3, ms: 1200 },
  { kind: 'narration', text: '唸り声だけが、返ってきた。' },
  { kind: 'voice', text: '——ほらね。言葉も、もう無い。', pace: 'slow', pre: 500 },
  // 言い終わる前に、 声がさえぎる (auto で次の行へ)。 一瞬だけ空気の音を止める。
  { kind: 'cue', effect: 'amb:off' },
  { kind: 'lunaire', speech: 'spoken', text: 'ずっと、ひとりで——', auto: 350 },
  { kind: 'cue', effect: 'amb:adv_awoken_hall_air' },
  { kind: 'voice', text: '——大丈夫。あなたが、楽にしてあげられる。' },
  { kind: 'picture', src: '/adv/hound/black.webp', fadeMs: 0 }, { kind: 'stage', effect: 'zoom', scale: 1, x: .5, y: .5, ms: 1 },
  { kind: 'figure', src: '/adv/awoken/beast_crouch.webp', x: .5, bottom: .967, height: 1, fadeMs: 0 },
  { kind: 'stage', effect: 'sink', power: 18, ms: 260 },
  { kind: 'cue', effect: 'sfx:adv_awoken_crouch' }, { kind: 'stage', effect: 'shake', power: 8, ms: 320 },
  { kind: 'narration', text: '獣が、身を低く沈めた。', pre: 150 },
  { kind: 'stage', effect: 'zoom', scale: 1.24, x: .5, y: .328, ms: 140 },
  { kind: 'cue', effect: 'sfx:awoken_voice_haunt_roar' }, { kind: 'cue', effect: 'sfx:adv_awoken_stones_fall' },
  { kind: 'stage', effect: 'shake', power: 18, ms: 520 }, { kind: 'stage', effect: 'flash' },
  { kind: 'narration', text: '獣が吠え、割れた天井から石が落ちた。', pre: 250 },
  { kind: 'cue', effect: 'blackout' },
  { kind: 'beat', ms: 300 },
]

function getAwokenPreBattleLines(campaign: CampaignState): AdvLine[] {
  const choice = campaign.choicesByBossId?.['dream-hound']
  const crown = choice === 'true' || choice === 'loop' ? [AWOKEN_PREBATTLE_CROWN_LINE[choice]] : []
  return [...AWOKEN_PREBATTLE_HEAD, { kind: 'cue', effect: 'sfx:adv_awoken_crown_creak' }, ...crown, ...AWOKEN_PREBATTLE_TAIL]
}

const RAW_PREBATTLE: Record<string, AdvLine[]> = {
  'nameless-shade': [
    { kind: 'narration', text: '廊下の奥に、影のような者が立っていた。' },
    { kind: 'narration', text: '顔は無い。怒ってもいない。ただ、手をこちらへ伸ばしてくる。' },
    { kind: 'lunaire', text: '……終わらせてあげる。' },
  ],
  'dream-hound': [
    // 2026-10-27 下書き3版＝docs/research/2026-10-27-hound-adv-draft.md（試作で作者が通した台詞と絵のまま）。
    //   獣の顔は描かない (王冠と口の闇だけ)。 寄りの中心 (x,y) は試作の「寄り＋ずらし」を同じ見え方になるよう置き直した値。
    //   「主、いる。」の前で広間の音を消して無音にし、 鎖が張る所でいちばん強い揺れと白い一瞬を1回だけ出す。
    { kind: 'cue', effect: 'amb:adv_hound_hall_air' }, { kind: 'beat', ms: 1200 },   // 音の下敷き (2026-10-28): 効果音より先に部屋の空気と遠くの曲を入れる
    { kind: 'cue', effect: 'sfx:adv_hound_door' },
    { kind: 'narration', text: '扉が、ひとりでに開いた。', pre: 300 },
    { kind: 'picture', src: '/adv/hound/throne_empty.webp', fadeMs: 600 },
    { kind: 'stage', effect: 'zoom', scale: 1, x: .5, y: .011, ms: 1 }, { kind: 'stage', effect: 'zoom', scale: 1.1, x: .5, y: .011, ms: 5200 },
    { kind: 'cue', effect: 'amb:adv_hound_hall_air' },
    { kind: 'narration', text: '長い階段の上に、玉座があった。', pre: 1100 },
    { kind: 'narration', text: '誰も、座っていなかった。' },
    { kind: 'voice', text: '——空いてるね。座っちゃう？', pace: 'quick' },
    { kind: 'lunaire', speech: 'inner', text: '座らないよ。' },
    { kind: 'picture', src: '/adv/hound/beast_guard.webp', fadeMs: 0 },
    { kind: 'stage', effect: 'zoom', scale: 1, x: .058, y: .697, ms: 1 },
    { kind: 'cue', effect: 'sfx:adv_hound_chain_small' },
    { kind: 'narration', text: '階段の下で、黒い獣が玉座に背を向けて伏せていた。', pre: 300 },
    { kind: 'stage', effect: 'zoom', scale: 1.06, x: .058, y: .697, ms: 2400 },
    { kind: 'narration', text: '王冠が、顔を覆うほど深くはまっていた。' },
    { kind: 'cue', effect: 'sfx:adv_hound_chain_drag' },
    { kind: 'narration', text: '足首の鎖が、床にたるんでいた。' },
    // 割り込みの寄り: 王冠の絵へ切り替え、 大きく寄った所から 0.15 秒で戻して止める。
    { kind: 'picture', src: '/adv/hound/crown_turn.webp', fadeMs: 0 },
    { kind: 'stage', effect: 'zoom', scale: 1.38, x: .5, y: .5, ms: 1 }, { kind: 'stage', effect: 'zoom', scale: 1, x: .5, y: .5, ms: 150 },
    { kind: 'cue', effect: 'sfx:adv_hound_chain_crown' },
    { kind: 'narration', text: '王冠が、こちらを向いた。', pre: 250 },
    { kind: 'cue', effect: 'sfx:adv_hound_rumble' }, { kind: 'stage', effect: 'shake', power: 3, ms: 1200 },
    { kind: 'boss', name: '獣', text: '主の、前。' },
    { kind: 'lunaire', speech: 'spoken', text: '誰も、いないよ。' },
    { kind: 'picture', src: '/adv/hound/beast_gaze.webp', fadeMs: 0 },
    { kind: 'stage', effect: 'zoom', scale: 1, x: .5, y: .05, ms: 1 }, { kind: 'stage', effect: 'zoom', scale: 1.08, x: .5, y: .05, ms: 4000 },
    { kind: 'cue', effect: 'sfx:adv_hound_chain_small' },
    { kind: 'narration', text: '王冠が、玉座のほうを向いた。' },
    { kind: 'cue', effect: 'amb:off' },
    { kind: 'boss', name: '獣', text: '主、いる。', pre: 500 },
    // 伏せ直す: 絵を人物絵として立て、 少しだけ沈める (後ろは黒・試作と同じ)。
    { kind: 'picture', src: '/adv/hound/black.webp', fadeMs: 0 },
    { kind: 'stage', effect: 'zoom', scale: 1, x: .5, y: .5, ms: 1 },
    { kind: 'figure', src: '/adv/hound/beast_guard.webp', x: .5, bottom: 1, height: 1, fadeMs: 0 },
    { kind: 'stage', effect: 'sink', power: 6, ms: 700 },
    { kind: 'cue', effect: 'sfx:adv_hound_chain_small' }, { kind: 'cue', effect: 'amb:adv_hound_hall_air' },
    { kind: 'narration', text: '頭を下げ、前足の上に載せた。' },
    { kind: 'lunaire', speech: 'spoken', text: 'ずっと、ひとりで、待ってたんだね。' },
    { kind: 'voice', text: '——そう。あなたが、楽にしてあげられる。' },
    { kind: 'picture', src: '/adv/hound/throne_empty.webp', fadeMs: 0 },
    { kind: 'figure', src: null, fadeMs: 0 },
    { kind: 'stage', effect: 'zoom', scale: 1, x: .5, y: -.433, ms: 1 }, { kind: 'stage', effect: 'zoom', scale: 1.05, x: .5, y: -.433, ms: 260 },
    { kind: 'cue', effect: 'sfx:adv_hound_footstep' },
    { kind: 'narration', text: '玉座のほうへ、一歩踏み出した。' },
    // 立ちはだかる: 下から 0.3 秒で持ち上がる (後ろは黒)。
    { kind: 'picture', src: '/adv/hound/black.webp', fadeMs: 0 },
    { kind: 'stage', effect: 'zoom', scale: 1, x: .5, y: .5, ms: 1 },
    { kind: 'figure', src: '/adv/hound/beast_block.webp', x: .5, bottom: 1.078, height: 1, fadeMs: 0 },
    { kind: 'stage', effect: 'sink', power: -42, ms: 300 },
    { kind: 'cue', effect: 'sfx:adv_hound_rise' }, { kind: 'stage', effect: 'shake', power: 8, ms: 320 },
    { kind: 'narration', text: '獣が立ち上がり、階段の前をふさいだ。', pre: 200 },
    { kind: 'stage', effect: 'zoom', scale: 1.14, x: .5, y: .229, ms: 120 },
    { kind: 'cue', effect: 'sfx:adv_hound_rumble' }, { kind: 'stage', effect: 'shake', power: 3, ms: 1200 },
    { kind: 'boss', name: '獣', text: '下がれ。' },
    { kind: 'stage', effect: 'zoom', scale: 1.26, x: .5, y: .284, ms: 140 },
    { kind: 'cue', effect: 'sfx:adv_hound_chain_taut' }, { kind: 'stage', effect: 'shake', power: 18, ms: 520 }, { kind: 'stage', effect: 'flash' },
    { kind: 'narration', text: '獣が踏み込み、鎖が張った。' },
    { kind: 'cue', effect: 'blackout' },
    { kind: 'beat', ms: 300 },
  ],
  'bride': [
    // 2026-10-27 下書き3版＝docs/research/2026-10-26-bride-adv-draft.md。 時計の音なし・花嫁の顔は描かない・光らせない。
    //   演出＝docs/research/2026-10-27-adv-emotion-staging.md（作者 10-27 の指示で「揺れ0回」をやめた）。
    //   針へゆっくり寄る → 座らされる所で小さく揺れる → 「待って。」で急に寄る → 待っていた話で震えが強まる
    //   → 手首に食い込む所でいちばん強い揺れと白い一瞬を1回だけ (それまで取っておく)。
    //   人物絵の寄り: 手元が文字の枠 (画面の 0.74 から下) に隠れない高さに置く。
    { kind: 'picture', src: '/adv/bride/hall_full.webp' }, { kind: 'cue', effect: 'amb:adv_bride_hall_air' },   // 音の下敷き (2026-10-28)
    { kind: 'narration', text: '廊下を抜けると、大時計のある広間に出た。', pre: 500 },
    { kind: 'stage', effect: 'zoom', scale: 1.4, y: .12, ms: 2600 },
    { kind: 'narration', text: '針は、婚礼の刻で止まっていた。' },
    { kind: 'stage', effect: 'zoom', scale: 1.15, y: .4, ms: 1400 },
    { kind: 'narration', text: '長い卓の上座に、椅子が二つ並んでいた。' },
    { kind: 'voice', text: '——わ、ごちそう。……の、跡だね。', pace: 'quick' },
    { kind: 'stage', effect: 'zoom', scale: 1, ms: 1 },
    { kind: 'figure', src: '/adv/bride/fig_welcome.webp', x: .5, bottom: 1.02, height: .98 },
    { kind: 'boss', name: '花嫁', text: '……あなた。来て、くれたのね。', pre: 200 },
    { kind: 'boss', name: '花嫁', text: '遅かったわ。ほら、座って。' },
    { kind: 'picture', src: '/adv/bride/hall_seat.webp', fadeMs: 250 },
    { kind: 'figure', src: '/adv/bride/fig_welcome.webp', x: .5, bottom: 1.4, height: 1.5 },
    { kind: 'cue', effect: 'sfx:adv_chair_pull' }, { kind: 'stage', effect: 'shake', power: 5, ms: 260 },
    { kind: 'narration', text: '冷たい手に引かれて、上座の椅子に座らされた。' },
    { kind: 'cue', effect: 'sfx:adv_rustle' },
    { kind: 'boss', name: '花嫁', text: '襟が曲がってる。' },
    { kind: 'voice', text: '——あなた、花婿になっちゃったね。', pace: 'quick' },
    { kind: 'lunaire', speech: 'spoken', text: 'あの、人違い——', auto: 350 },
    { kind: 'boss', name: '花嫁', text: '婚礼の刻までには戻るって、言ったでしょう。' },
    { kind: 'lunaire', speech: 'inner', text: '花婿さんだと、思われてる……' },
    { kind: 'lunaire', speech: 'spoken', text: '本物の花婿さん、捜してくるね。' },
    { kind: 'cue', effect: 'sfx:adv_chair_scrape' },
    { kind: 'narration', text: '立ち上がりかけた。' },
    { kind: 'figure', src: '/adv/bride/fig_cling.webp', x: .5, bottom: 1.3, height: 1.5, fadeMs: 0 },
    { kind: 'stage', effect: 'zoom', scale: 1.1, y: .4, ms: 90 }, { kind: 'stage', effect: 'hop', power: 10 },
    { kind: 'boss', name: '花嫁', text: '待って。', pace: 'rush' },
    { kind: 'cue', effect: 'sfx:adv_cloth_pull' },
    { kind: 'narration', text: '袖を、つかまれた。' },
    { kind: 'stage', effect: 'sink', power: 14, ms: 700 },
    { kind: 'boss', name: '花嫁', text: '……あの時も、そう言ったわ。' },
    { kind: 'beat', ms: 400 },
    { kind: 'stage', effect: 'zoom', scale: 1.2, y: .4, ms: 1800 }, { kind: 'stage', effect: 'tremble', power: 2, ms: 1600 },
    { kind: 'boss', name: '花嫁', text: 'ずっと、ずっと待っていたのよ。', pace: 'rush' },
    { kind: 'stage', effect: 'tremble', power: 3, ms: 1600 },
    { kind: 'boss', name: '花嫁', text: 'どうして、また離れようとするの。', pace: 'rush' },
    { kind: 'stage', effect: 'zoom', scale: 1, ms: 1 },
    { kind: 'figure', src: '/adv/bride/fig_cling.webp', x: .5, bottom: 1.38, height: 1.8 },
    { kind: 'stage', effect: 'tremble', power: 5, ms: 1800 },
    { kind: 'boss', name: '花嫁', text: '行かないで。ここにいて。', pace: 'rush' },
    { kind: 'stage', effect: 'zoom', scale: 1.12, y: .4, ms: 2200 },
    { kind: 'boss', name: '花嫁', text: '……もう、離さない。' },
    { kind: 'beat', ms: 300 },
    { kind: 'cue', effect: 'sfx:adv_wrist_grip' }, { kind: 'stage', effect: 'shake', power: 18, ms: 520 }, { kind: 'stage', effect: 'flash' },
    { kind: 'narration', text: '指が、手首に食い込んだ。' },
    { kind: 'beat', ms: 250 },
    { kind: 'cue', effect: 'blackout' },
    { kind: 'beat', ms: 450 },
  ],
  'vigil-knight': [
    // 2026-10-28 下書き5版＝docs/research/2026-10-27-knight-adv-draft.md（試作で作者が通した台詞と絵のまま）。音 (adv_knight_*) = 2026-10-28 scripts/render_adv_sfx.py (SOUNDS_KA)。
    //   寄りの中心 (x,y) は試作の「寄り＋ずらし」を同じ見え方になるよう置き直した値。
    //   試作の「暗い騎士がだんだん見えてくる」は明るさを変える手が無いので、 広間の絵からゆっくり重ねて出す形にした。
    //   錠と「……お休みだ。」で広間の空気だけをゆっくり引く (曲は遠くで鳴らしたまま)。 石突きが床を打つ所でいちばん強い揺れと白い一瞬と一打の音を1回だけ出す。
    { kind: 'picture', src: '/adv/knight/hall_door.webp', fadeMs: 800 },
    { kind: 'stage', effect: 'zoom', scale: 1, x: .5, y: .085, ms: 1 }, { kind: 'stage', effect: 'zoom', scale: 1.12, x: .5, y: .085, ms: 5200 },
    { kind: 'cue', effect: 'amb:adv_knight_hall_air' },
    { kind: 'narration', text: '広間の奥に、鎖を巻かれた扉があった。', pre: 1300 },
    { kind: 'picture', src: '/adv/knight/knight_stand.webp', fadeMs: 2400 },
    { kind: 'stage', effect: 'zoom', scale: 1, x: .5, y: .211, ms: 1 }, { kind: 'stage', effect: 'zoom', scale: 1.04, x: .5, y: .211, ms: 6000 },
    { kind: 'narration', text: 'その前に、誰かが立っていた。', pre: 700 },
    { kind: 'narration', text: '人の形をしていた。長い槍を、床に立てていた。' },
    { kind: 'stage', effect: 'zoom', scale: 1.1, x: .5, y: .317, ms: 120 },
    { kind: 'cue', effect: 'sfx:exec_unique_knight_stride' },
    { kind: 'boss', name: '騎士', text: '——何者だ。主の御前であるぞ。', pre: 200 },
    { kind: 'narration', text: '鎧は、ひどく錆びていた。' },
    { kind: 'narration', text: '足もとの床にだけ、埃が積もっていなかった。' },
    { kind: 'lunaire', speech: 'spoken', text: '通りたいだけ。扉には、触らないよ。' },
    { kind: 'boss', name: '騎士', text: 'ならん。', pre: 400 },
    { kind: 'voice', text: '——頑固ねえ。', pace: 'quick' },
    { kind: 'stage', effect: 'zoom', scale: 1.14, x: .5, y: .337, ms: 260 },
    { kind: 'cue', effect: 'sfx:adv_knight_footstep' },
    { kind: 'narration', text: '一歩、前へ出た。' },
    // 割り込みの寄り: 兜の絵へ切り替え、 大きく寄った所から 0.15 秒で戻して止める。
    { kind: 'picture', src: '/adv/knight/helm_turn.webp', fadeMs: 0 },
    { kind: 'stage', effect: 'zoom', scale: 1.38, x: .5, y: .5, ms: 1 }, { kind: 'stage', effect: 'zoom', scale: 1, x: .5, y: .5, ms: 150 },
    { kind: 'narration', text: '兜が、ほんの少しだけ、扉のほうへ向いた。', pre: 250 },
    { kind: 'picture', src: '/adv/knight/chain_lock.webp', fadeMs: 0 },
    { kind: 'cue', effect: 'amb:off' },
    { kind: 'narration', text: '鎖の錠に、埃が積もっていた。', pre: 200 },
    { kind: 'picture', src: '/adv/knight/knight_stand.webp', fadeMs: 0 },
    { kind: 'stage', effect: 'zoom', scale: 1.14, x: .5, y: .337, ms: 1 },
    { kind: 'cue', effect: 'amb:adv_knight_hall_air' },
    { kind: 'lunaire', speech: 'spoken', text: '主さまは、中にいるの？', pre: 300 },
    { kind: 'cue', effect: 'amb:off' },
    { kind: 'boss', name: '騎士', text: '……お休みだ。', pre: 600 },
    { kind: 'cue', effect: 'amb:adv_knight_hall_air' },
    { kind: 'lunaire', speech: 'spoken', text: '錠に、埃が積もってるよ。', pre: 300 },
    { kind: 'lunaire', speech: 'inner', text: '……ずっと、ひとりで、構えていた……' },
    { kind: 'voice', text: '——そう。あなたなら、休ませてあげられる。' },
    { kind: 'lunaire', speech: 'spoken', text: '起こさないように、そっと見るだけ。' },
    { kind: 'stage', effect: 'zoom', scale: 1.2, x: .5, y: .353, ms: 500 },
    { kind: 'cue', effect: 'sfx:adv_knight_footstep' }, { kind: 'beat', ms: 420 }, { kind: 'cue', effect: 'sfx:adv_knight_footstep' },
    { kind: 'narration', text: '扉のほうへ、歩き出した。' },
    // 槍を向ける: 絵を人物絵として立て、 下から 0.3 秒で持ち上げる (後ろは黒)。
    { kind: 'picture', src: '/adv/knight/black.webp', fadeMs: 0 },
    { kind: 'stage', effect: 'zoom', scale: 1, x: .5, y: .5, ms: 1 },
    { kind: 'figure', src: '/adv/knight/lance_point.webp', x: .5, bottom: 1.078, height: 1, fadeMs: 0 },
    { kind: 'stage', effect: 'sink', power: -42, ms: 300 },
    { kind: 'cue', effect: 'sfx:knight_sweep' }, { kind: 'stage', effect: 'shake', power: 8, ms: 320 },
    { kind: 'narration', text: '騎士が槍を下ろし、穂先をこちらへ向けた。', pre: 200 },
    { kind: 'stage', effect: 'zoom', scale: 1.14, x: .5, y: .229, ms: 120 },
    { kind: 'boss', name: '騎士', text: 'ここは、通さん。たとえ誰であろうと。' },
    { kind: 'stage', effect: 'zoom', scale: 1.26, x: .5, y: .285, ms: 140 },
    { kind: 'cue', effect: 'sfx:adv_knight_butt_strike' }, { kind: 'stage', effect: 'shake', power: 18, ms: 520 }, { kind: 'stage', effect: 'flash' },   // 石突きの音 (10-28 夜・作者「音がないのはもったいない」)
    { kind: 'narration', text: '石突きが、床を打った。' },
    { kind: 'cue', effect: 'blackout' },
    { kind: 'beat', ms: 250 },
  ],
  'the-executioner': [
    // 2026-10-27 版4＝docs/research/2026-10-27-executioner-adv-draft.md（試作で作者が通した台詞と絵のまま）。
    //   最初は遠くの後ろ姿。 判決書を読み上げる声が LUNAIRE の名で止まってから振り向く (振り向くまで名札は「低い声」)。
    //   寄りの中心 (x,y) は試作の「寄り＋ずらし」を同じ見え方になるよう置き直した値。 判決書の寄りは絵を差し替えて寄る。
    //   刃の腕が持ち上がる所で中くらいの揺れ、 踏み込みでいちばん強い揺れと白い一瞬を1回だけ。
    //   音 (adv_exec_*) = 2026-10-28 scripts/render_adv_sfx.py。 部屋の空気を頭から敷き、 扉はその 1.2 秒後。
    { kind: 'picture', src: '/adv/executioner/hall_far.webp', fadeMs: 800 }, { kind: 'cue', effect: 'amb:adv_exec_hall_air' },   // 音の下敷き (2026-10-28)
    { kind: 'stage', effect: 'zoom', scale: 1, x: .753, y: .35, ms: 1 }, { kind: 'stage', effect: 'zoom', scale: 1.08, x: .753, y: .35, ms: 8000 },
    { kind: 'beat', ms: 1200 }, { kind: 'cue', effect: 'sfx:adv_exec_door' },
    { kind: 'narration', text: '扉の向こうは、広い部屋だった。', pre: 1800 },
    { kind: 'narration', text: '奥の壁に沿って、寝台が並んでいた。どれにも、白い布が、頭まで掛けられていた。' },
    { kind: 'narration', text: '寝台の前に、誰かが立っていた。こちらに、背を向けていた。' },
    { kind: 'cue', effect: 'sfx:adv_exec_paper_flip' },
    { kind: 'narration', text: '低い声が、何かを読み上げていた。' },
    { kind: 'picture', src: '/adv/executioner/reading_back.webp', fadeMs: 0 },
    { kind: 'stage', effect: 'zoom', scale: 1, x: .5, y: .5, ms: 1 },
    { kind: 'boss', name: '低い声', text: '門番。夜のうちに門を開け、賊を引き入れた罪。', pace: 'slow', pre: 400 },
    { kind: 'narration', text: '一つ読むと、寝台を一つ見た。' },
    { kind: 'cue', effect: 'sfx:adv_exec_paper_flip' },
    { kind: 'boss', name: '低い声', text: '厨の娘。主の杯に、毒を入れた罪。', pace: 'slow' },
    { kind: 'cue', effect: 'sfx:adv_exec_paper_flip' },
    { kind: 'boss', name: '低い声', text: '書庫番。書庫に火を放った罪。', pace: 'slow' },
    { kind: 'stage', effect: 'zoom', scale: 1.08, x: 1, y: .5, ms: 3000 },
    { kind: 'narration', text: '読み上げた数だけ、寝台が埋まっていた。' },
    { kind: 'cue', effect: 'amb:off' },
    { kind: 'boss', name: '低い声', text: 'LUNAIRE。', pre: 600 },
    { kind: 'narration', text: '名前のあとに、罪は続かなかった。', pre: 800 },
    { kind: 'picture', src: '/adv/executioner/turn_verdict.webp', fadeMs: 0 },
    { kind: 'stage', effect: 'zoom', scale: 1, x: .5, y: .5, ms: 1 },
    { kind: 'cue', effect: 'sfx:adv_exec_robe' },
    { kind: 'narration', text: '男が、振り向いた。', pre: 200 },
    { kind: 'boss', name: '介錯', text: '主の判決である。' },
    // 判決書の印: 絵を 0.4 秒で重ね、 印の無い最後の行へ 0.15 秒で寄って止まる。
    { kind: 'picture', src: '/adv/executioner/verdict_seals.webp', fadeMs: 400 },
    { kind: 'cue', effect: 'sfx:adv_exec_paper_turn' },
    { kind: 'narration', text: '男は、紙をこちらへ向けた。並んだ名前の横に、一つずつ、黒い印が押してあった。' },
    { kind: 'stage', effect: 'zoom', scale: 1.2, x: .275, y: .433, ms: 150 },
    { kind: 'narration', text: '最後の名前の横だけ、何も押されていなかった。', pre: 250 },
    { kind: 'lunaire', speech: 'spoken', text: '私の罪は、何？' },
    { kind: 'picture', src: '/adv/executioner/turn_verdict.webp', fadeMs: 0 },
    { kind: 'stage', effect: 'zoom', scale: 1, x: .5, y: .5, ms: 1 },
    { kind: 'cue', effect: 'amb:adv_exec_hall_air' },
    { kind: 'boss', name: '介錯', text: '罪を問うのは、私の役ではない。' },
    { kind: 'cue', effect: 'sfx:adv_exec_paper_fold' },
    { kind: 'boss', name: '介錯', text: '判決書に名のある者は、すべて執行する。' },
    { kind: 'picture', src: '/adv/executioner/point_bed.webp', fadeMs: 0 },
    { kind: 'stage', effect: 'zoom', scale: 1, x: .85, y: .396, ms: 1 }, { kind: 'stage', effect: 'zoom', scale: 1.12, x: .85, y: .396, ms: 6000 },
    { kind: 'cue', effect: 'sfx:adv_exec_blade_swish' },
    { kind: 'narration', text: '刃の腕が、奥を指した。寝台が一つだけ、空いていた。' },
    { kind: 'boss', name: '介錯', text: 'そちらへ。' },
    { kind: 'voice', text: '——……ご丁寧ね。あなたの分まで。', pace: 'quick', pre: 1200 },
    { kind: 'lunaire', speech: 'spoken', text: 'ごめんね。そこには、寝ない。' },
    // 刃を掲げる: 絵を人物絵として立て、 下から 0.4 秒で持ち上げる (後ろは黒・試作と同じ)。
    { kind: 'picture', src: '/adv/executioner/black.webp', fadeMs: 0 },
    { kind: 'stage', effect: 'zoom', scale: 1, x: .5, y: .5, ms: 1 },
    { kind: 'figure', src: '/adv/executioner/pic4.webp', x: .5, bottom: 1.044, height: 1, fadeMs: 0 },
    { kind: 'stage', effect: 'sink', power: -24, ms: 400 },
    { kind: 'cue', effect: 'sfx:adv_exec_blade_ring' }, { kind: 'stage', effect: 'shake', power: 8, ms: 320 },
    { kind: 'narration', text: '刃の腕が、ゆっくりと、持ち上がった。', pre: 300 },
    { kind: 'stage', effect: 'zoom', scale: 1.14, x: .5, y: .138, ms: 120 },
    { kind: 'boss', name: '介錯', text: '執行する。' },
    { kind: 'stage', effect: 'zoom', scale: 1.26, x: .5, y: .231, ms: 140 },
    { kind: 'cue', effect: 'sfx:adv_exec_stomp' }, { kind: 'stage', effect: 'shake', power: 18, ms: 520 }, { kind: 'stage', effect: 'flash' },
    { kind: 'narration', text: '男が、踏み込んだ。' },
    { kind: 'cue', effect: 'blackout' },
    { kind: 'beat', ms: 300 },
  ],
  // 戴冠獣で選んだ答えで王冠の一文だけ変わる＝getAwokenPreBattleLines。 ここは選んでいない時 (王冠の一文なし)。
  'crowned-king-awoken': [...AWOKEN_PREBATTLE_HEAD, ...AWOKEN_PREBATTLE_TAIL],
  'inverted-jester': [
    // 2026-10-27 下書き5版＝docs/research/2026-10-27-jester-adv-draft.md（試作で作者が通した台詞と絵のまま）。
    //   寄りの中心 (x,y) は試作の「寄り＋ずらし」を同じ見え方になるよう置き直した値。 取り乱す所は早口 (rush)。
    //   「朝が、来ないの」の前で風を消して無音にし、 飛びかかる所でいちばん強い揺れと白い一瞬を1回だけ出す。
    { kind: 'picture', src: '/adv/jester/black.webp', fadeMs: 0 },
    { kind: 'cue', effect: 'amb:adv_jester_wind' }, { kind: 'beat', ms: 1200 },   // 音の下敷き (2026-10-28): 効果音より先に部屋の空気と遠くの曲を入れる
    { kind: 'cue', effect: 'sfx:adv_jester_steps_stop' },
    { kind: 'narration', text: '螺旋階段を、上りきった。', pre: 300 },
    { kind: 'picture', src: '/adv/jester/east_sky.webp', fadeMs: 600 },
    { kind: 'stage', effect: 'zoom', scale: 1, x: .5, y: .133, ms: 1 }, { kind: 'stage', effect: 'zoom', scale: 1.1, x: .5, y: .133, ms: 5200 },
    { kind: 'cue', effect: 'amb:adv_jester_wind' },
    { kind: 'narration', text: '鐘楼の頂上に出た。風が、強く吹いていた。', pre: 1100 },
    { kind: 'narration', text: '空は、暗かった。東の端にも、色は無かった。' },
    { kind: 'picture', src: '/adv/jester/counting.webp', fadeMs: 0 },
    { kind: 'stage', effect: 'zoom', scale: 1, x: .5, y: .696, ms: 1 },
    { kind: 'narration', text: '吊られた鐘の下に、獣がうずくまっていた。', pre: 300 },
    { kind: 'stage', effect: 'zoom', scale: 1.06, x: .5, y: .696, ms: 2400 },
    { kind: 'cue', effect: 'sfx:adv_jester_rope_creak' },
    { kind: 'narration', text: '腰に、鐘の綱を巻きつけていた。' },
    { kind: 'narration', text: '綱には、結び目がいくつも並んでいた。' },
    { kind: 'boss', name: '鶏', text: '……みっつ。……よっつ。' },
    { kind: 'narration', text: '小さな声で、何かを数えていた。' },
    { kind: 'stage', effect: 'zoom', scale: 1.1, x: .5, y: .622, ms: 260 },
    { kind: 'cue', effect: 'sfx:adv_jester_footstep' },
    { kind: 'narration', text: '一歩、近づいた。' },
    // 振り向く: 震えは人物絵にだけ効くので、 振り向いた絵を人物絵として立てる (後ろは黒・試作と同じ見え方)。
    { kind: 'picture', src: '/adv/jester/black.webp', fadeMs: 0 },
    { kind: 'figure', src: '/adv/jester/turn.webp', x: .5, bottom: 1, height: 1, fadeMs: 0 },
    { kind: 'stage', effect: 'zoom', scale: 1, x: .5, y: .319, ms: 1 }, { kind: 'stage', effect: 'zoom', scale: 1.14, x: .5, y: .319, ms: 120 },
    { kind: 'cue', effect: 'sfx:adv_jester_feathers' },
    { kind: 'boss', name: '鶏', text: '……いつ——', pre: 250 },
    { kind: 'stage', effect: 'tremble' },
    { kind: 'boss', name: '鶏', text: 'あっ……ああっ！　ずれた、ずれたっ！', pace: 'rush' },
    { kind: 'stage', effect: 'tremble' },
    { kind: 'boss', name: '鶏', text: 'また破った！　また、私がっ……！', pace: 'rush' },
    { kind: 'stage', effect: 'tremble' },
    { kind: 'boss', name: '鶏', text: '決まりの数まで数えて、綱に結び目を作って……それから鳴くの！　それが決まりなのっ！', pace: 'rush' },
    { kind: 'stage', effect: 'tremble' }, { kind: 'stage', effect: 'zoom', scale: 1.2, x: .5, y: .333, ms: 120 },
    { kind: 'boss', name: '鶏', text: 'なのに、あの夜……数えきる前に、鳴いちゃった……！', pace: 'rush' },
    { kind: 'cue', effect: 'amb:off' },
    { kind: 'boss', name: '鶏', text: 'あの夜から……っ、朝が、来ないの……！', pre: 500 },
    { kind: 'cue', effect: 'amb:adv_jester_wind' }, { kind: 'stage', effect: 'tremble' },
    { kind: 'boss', name: '鶏', text: 'だから数え直してるのっ！　今度こそ、ひとつも間違えずに！', pace: 'rush' },
    { kind: 'lunaire', speech: 'spoken', text: '鳴いたから、朝が来ないの？' },
    { kind: 'stage', effect: 'zoom', scale: 1.28, x: .5, y: .348, ms: 120 }, { kind: 'stage', effect: 'tremble' },
    { kind: 'boss', name: '鶏', text: '私のせいなのっ！', pace: 'rush' },
    { kind: 'boss', name: '鶏', text: '私のせいで、いいの！　いいんだってばっ！', pace: 'rush' },
    { kind: 'picture', src: '/adv/jester/counting.webp', fadeMs: 0 },
    { kind: 'figure', src: null, fadeMs: 0 },
    { kind: 'stage', effect: 'zoom', scale: 1.06, x: .5, y: .696, ms: 1 }, { kind: 'stage', effect: 'zoom', scale: 1, x: .5, y: .696, ms: 1600 },
    { kind: 'cue', effect: 'sfx:adv_jester_rope_creak' },
    { kind: 'narration', text: '獣は鐘のほうへ向き直り、また最初から数え始めた。' },
    { kind: 'boss', name: '鶏', text: '……ひとつ。……ふたつ。……み——' },
    // 割り込みの寄り: 揺れる鐘の絵へ切り替え、 大きく寄った所から 0.15 秒で戻して止める。 鐘は打たない (余韻だけ)。
    { kind: 'picture', src: '/adv/jester/bell_sway.webp', fadeMs: 0 },
    { kind: 'stage', effect: 'zoom', scale: 1.38, x: .5, y: .5, ms: 1 }, { kind: 'stage', effect: 'zoom', scale: 1, x: .5, y: .5, ms: 150 },
    { kind: 'cue', effect: 'sfx:adv_jester_bell_hum' }, { kind: 'stage', effect: 'shake', power: 3, ms: 1200 },
    { kind: 'narration', text: '風が、鐘を揺らした。', pre: 200 },
    { kind: 'boss', name: '鶏', text: 'いやっ……鳴らないで、鳴らないでっ！', pace: 'rush' },
    { kind: 'picture', src: '/adv/jester/counting.webp', fadeMs: 0 },
    { kind: 'voice', text: '——気にしないで。そういう子なの。', pace: 'quick' },
    { kind: 'lunaire', speech: 'inner', text: '……ずっと、ひとりで、数えていた……' },
    { kind: 'voice', text: '——そう。あなたが、終わらせてあげられる。' },
    { kind: 'lunaire', speech: 'spoken', text: '鐘、押さえとくね。' },
    { kind: 'stage', effect: 'zoom', scale: 1.06, x: .5, y: -.089, ms: 260 },
    { kind: 'cue', effect: 'sfx:adv_jester_footstep' },
    { kind: 'narration', text: '揺れる鐘を押さえようと、手を伸ばした。' },
    // 襲いかかる: 下から 0.3 秒で持ち上がる (後ろは黒)。
    { kind: 'picture', src: '/adv/jester/black.webp', fadeMs: 0 },
    { kind: 'stage', effect: 'zoom', scale: 1, x: .5, y: .5, ms: 1 },
    { kind: 'figure', src: '/adv/jester/lunge.webp', x: .5, bottom: 1.078, height: 1, fadeMs: 0 },
    { kind: 'stage', effect: 'sink', power: -42, ms: 300 },
    { kind: 'cue', effect: 'sfx:adv_jester_wings_spread' }, { kind: 'stage', effect: 'shake', power: 8, ms: 320 },
    { kind: 'boss', name: '鶏', text: 'やめてっ！　鐘に、触らないでっ！！', pre: 200, pace: 'rush' },
    { kind: 'stage', effect: 'zoom', scale: 1.2, x: .5, y: .333, ms: 140 },
    { kind: 'cue', effect: 'sfx:adv_jester_wing_beat' }, { kind: 'stage', effect: 'shake', power: 18, ms: 520 }, { kind: 'stage', effect: 'flash' },
    { kind: 'narration', text: '獣が翼を広げ、こちらへ飛びかかった。' },
    { kind: 'cue', effect: 'blackout' },
    { kind: 'beat', ms: 300 },
  ],
  'blade-dancer': [
    // 2026-10-27 下書き＝docs/research/2026-10-27-blade-adv-draft.md（試作で作者が通した台詞と絵のまま）。
    //   舞姫の口上は文字送りを遅く (上品に)、 拍手を断る返事だけ速く (返事が速すぎる)。 寄りの中心 (x,y) は試作の「寄り＋ずらし」と同じ見え方になる値。
    //   こちらを向く絵は人物絵として立て、 少し沈める。 舞が始まる所で中くらい、 刃が伸びてくる所でいちばん強い揺れと白い一瞬を1回だけ。
    { kind: 'cue', effect: 'amb:adv_blade_theater' }, { kind: 'beat', ms: 1200 },   // 音の下敷き (2026-10-28): 効果音より先に部屋の空気と遠くの曲を入れる
    { kind: 'cue', effect: 'sfx:adv_blade_curtain' },
    { kind: 'narration', text: '重い幕をくぐると、舞台に出た。', pre: 300 },
    { kind: 'picture', src: '/adv/blade/bow_to_seats.webp', fadeMs: 600 },
    { kind: 'stage', effect: 'zoom', scale: 1, x: .753, y: .2, ms: 1 }, { kind: 'stage', effect: 'zoom', scale: 1.08, x: .753, y: .2, ms: 5200 },
    { kind: 'cue', effect: 'amb:adv_blade_theater' },
    { kind: 'narration', text: '冷たい照明の下に、舞姫が立っていた。', pre: 1100 },
    { kind: 'cue', effect: 'sfx:adv_blade_hem' },
    { kind: 'narration', text: '両手に、刃を一振りずつ。目には、黒い布。' },
    { kind: 'boss', name: '舞姫', text: '今宵も、ようこそお越しくださいました。', pace: 'slow' },
    { kind: 'picture', src: '/adv/blade/empty_seats.webp', fadeMs: 0 },
    { kind: 'stage', effect: 'zoom', scale: 1.08, x: .5, y: .5, ms: 1 }, { kind: 'stage', effect: 'zoom', scale: 1, x: .5, y: .5, ms: 4500 },
    { kind: 'narration', text: '客席は、空だった。一席も、埋まっていない。', pre: 400 },
    { kind: 'boss', name: '舞姫', text: 'どうぞ、最後まで、ごゆるりと。', pace: 'slow' },
    { kind: 'picture', src: '/adv/blade/black.webp', fadeMs: 0 },
    { kind: 'stage', effect: 'zoom', scale: 1, x: .5, y: .5, ms: 1 },
    { kind: 'figure', src: '/adv/blade/face_us.webp', x: .5, bottom: 1, height: 1, fadeMs: 0 },
    { kind: 'stage', effect: 'sink', power: 6, ms: 700 },
    { kind: 'cue', effect: 'sfx:adv_blade_step' }, { kind: 'cue', effect: 'sfx:adv_blade_hem' },
    { kind: 'narration', text: '足音に、舞姫がこちらを向いた。', pre: 300 },
    { kind: 'boss', name: '舞姫', text: 'まあ。お客さまが、もうおひとり。', pace: 'slow' },
    { kind: 'figure', src: null, fadeMs: 0 },
    { kind: 'picture', src: '/adv/blade/hand_offer.webp', fadeMs: 0 },
    { kind: 'stage', effect: 'zoom', scale: 1.38, x: .5, y: .5, ms: 1 }, { kind: 'stage', effect: 'zoom', scale: 1, x: .5, y: .5, ms: 150 },
    { kind: 'cue', effect: 'sfx:adv_blade_rustle' },
    { kind: 'boss', name: '舞姫', text: 'どうぞ、お掛けを。', pace: 'slow', pre: 250 },
    { kind: 'picture', src: '/adv/blade/face_us.webp', fadeMs: 0 },
    { kind: 'lunaire', speech: 'spoken', text: '拍手、したほうがいい？' },
    { kind: 'boss', name: '舞姫', text: 'いいえ。そのようなものは、なくてよいのです。', pace: 'rush' },
    { kind: 'lunaire', speech: 'inner', text: '返事が、速かった……', pre: 500 },
    { kind: 'voice', text: '——そう。あなたが、終わらせてあげられる。' },
    { kind: 'picture', src: '/adv/blade/dance_turn.webp', fadeMs: 0 },
    { kind: 'cue', effect: 'sfx:adv_blade_stomp' }, { kind: 'stage', effect: 'shake', power: 8, ms: 320 },
    { kind: 'narration', text: '舞が、始まった。', pre: 200 },
    { kind: 'stage', effect: 'zoom', scale: 1.24, x: .5, y: .328, ms: 140 },
    { kind: 'cue', effect: 'sfx:adv_blade_whoosh' }, { kind: 'stage', effect: 'shake', power: 18, ms: 520 }, { kind: 'stage', effect: 'flash' },
    { kind: 'narration', text: 'ひと回りした刃が、こちらへ伸びてきた。' },
    { kind: 'cue', effect: 'blackout' },
    { kind: 'beat', ms: 300 },
  ],
  'former-princess': [
    // 2026-10-27 下書き＝docs/research/2026-10-27-princess-adv-draft.md（試作で作者が通した台詞と絵のまま）。
    //   最初の3行は暗いまま文字だけ (姿を見せる前に、 どんな相手か想像させる)。 舞台の女は影だけで出し、 「あ、来た来た。」で初めて姿を見せる。
    //   笑う先代は人物絵として立てる (跳ねるのは人物絵だけに効くため)。 揺れは最後の踏み込みの1回だけ。 寄りの中心 (x,y) は試作と同じ見え方になる値。
    { kind: 'picture', src: '/adv/princess/black.webp', fadeMs: 0 },
    { kind: 'cue', effect: 'amb:adv_princess_hum' }, { kind: 'beat', ms: 1200 },   // 音の下敷き (2026-10-28): 効果音より先に部屋の空気と遠くの曲を入れる
    { kind: 'cue', effect: 'sfx:adv_princess_corridor_step' },
    { kind: 'narration', text: '廊下の燭台は、どれも、灯し直されたばかりだった。', pre: 300 },
    { kind: 'cue', effect: 'amb:adv_princess_hum' },
    { kind: 'narration', text: '扉の向こうで、誰かが、鼻歌を歌っていた。', pre: 400 },
    { kind: 'cue', effect: 'sfx:adv_princess_door' }, { kind: 'cue', effect: 'amb:off' },
    { kind: 'narration', text: '重い扉を、押し開けた。' },
    { kind: 'picture', src: '/adv/princess/stage_silhouette.webp', fadeMs: 800 },
    { kind: 'stage', effect: 'zoom', scale: 1, x: .5, y: .38, ms: 1 }, { kind: 'stage', effect: 'zoom', scale: 1.1, x: .5, y: .38, ms: 6200 },
    { kind: 'cue', effect: 'amb:adv_princess_stage_air' },
    { kind: 'narration', text: '円い舞台が、広がっていた。客席は、無かった。', pre: 1300 },
    { kind: 'narration', text: '声は、聞こえなかった。', pre: 600 },
    { kind: 'stage', effect: 'zoom', scale: 1.32, x: .5, y: .41, ms: 800 },
    { kind: 'cue', effect: 'sfx:adv_princess_step' },
    { kind: 'narration', text: '中央で、女が座って待っていた。' },
    { kind: 'picture', src: '/adv/princess/black.webp', fadeMs: 0 },
    { kind: 'stage', effect: 'zoom', scale: 1, x: .5, y: .5, ms: 1 },
    { kind: 'figure', src: '/adv/princess/smile.webp', x: .5, bottom: 1, height: 1, fadeMs: 0 },
    { kind: 'stage', effect: 'hop' },
    { kind: 'boss', name: '先代', text: 'あ、来た来た。', pace: 'quick', pre: 200 },
    { kind: 'boss', name: '先代', text: '待ってたんだよ。ずっと。' },
    { kind: 'cue', effect: 'sfx:adv_princess_twirl' },
    { kind: 'narration', text: '女は、レイピアを、くるりと回した。' },
    { kind: 'figure', src: null, fadeMs: 0 },
    { kind: 'picture', src: '/adv/princess/grip_cutin.webp', fadeMs: 0 },
    { kind: 'stage', effect: 'zoom', scale: 1.38, x: .5, y: .5, ms: 1 }, { kind: 'stage', effect: 'zoom', scale: 1, x: .5, y: .5, ms: 150 },
    { kind: 'cue', effect: 'sfx:adv_princess_grip' },
    { kind: 'narration', text: '構えが、LUNAIRE と、同じだった。', pre: 250 },
    { kind: 'picture', src: '/adv/princess/black.webp', fadeMs: 0 },
    { kind: 'figure', src: '/adv/princess/smile.webp', x: .5, bottom: 1, height: 1, fadeMs: 0 },
    { kind: 'narration', text: '女は、指を折って、数えはじめた。' },
    { kind: 'boss', name: '先代', text: '獣の子でしょ。騎士さんでしょ。花嫁さん。', pace: 'quick' },
    { kind: 'boss', name: '先代', text: 'お布団の人。起きちゃったワンちゃん。', pre: 400 },
    { kind: 'boss', name: '先代', text: 'それから……えっと……。', pace: 'slow', pre: 600 },
    { kind: 'stage', effect: 'hop' },
    { kind: 'boss', name: '先代', text: '……まあ、いっか。', pre: 300 },
    { kind: 'narration', text: '女は、折りかけた指を、そのまま下ろした。' },
    { kind: 'stage', effect: 'hop' },
    { kind: 'boss', name: '先代', text: 'みんな、楽にしてあげたんだよね。' },
    { kind: 'boss', name: '先代', text: 'すごいね。' },
    { kind: 'lunaire', speech: 'spoken', text: 'うん。みんな、楽にしてあげた。' },
    { kind: 'figure', src: null, fadeMs: 0 },
    { kind: 'picture', src: '/adv/princess/still_fingers.webp', fadeMs: 0 },
    { kind: 'stage', effect: 'zoom', scale: 1, x: .5, y: .2, ms: 1 }, { kind: 'stage', effect: 'zoom', scale: 1.08, x: .5, y: .2, ms: 3000 },
    { kind: 'cue', effect: 'amb:fade' },
    { kind: 'boss', name: '先代', text: '私もね、そう思ってた。楽にしてあげてるんだって。', pace: 'slow', pre: 400 },
    { kind: 'cue', effect: 'amb:off' },
    { kind: 'narration', text: '女の指が、止まった。' },
    // この部屋でいちばん重い所: 0.8秒なにも出さない。 揺らさない。 音も無いまま。
    { kind: 'boss', name: '先代', text: '……誰も、頼んでなかったのにね。', pace: 'slow', pre: 800 },
    { kind: 'picture', src: '/adv/princess/black.webp', fadeMs: 0 },
    { kind: 'stage', effect: 'zoom', scale: 1, x: .5, y: .5, ms: 1 },
    { kind: 'figure', src: '/adv/princess/smile.webp', x: .5, bottom: 1, height: 1, fadeMs: 0 },
    { kind: 'cue', effect: 'amb:adv_princess_stage_air' },
    { kind: 'narration', text: '女は、すぐに笑った。' },
    { kind: 'stage', effect: 'hop' },
    { kind: 'boss', name: '先代', text: 'あ、今のは、なし。', pace: 'quick' },
    { kind: 'lunaire', speech: 'spoken', text: '私も、頼まれてない……' },
    { kind: 'lunaire', speech: 'spoken', text: 'ずっと、ひとりで、', auto: 350 },
    { kind: 'figure', src: null, fadeMs: 0 },
    { kind: 'picture', src: '/adv/princess/salute.webp', fadeMs: 0 },
    { kind: 'stage', effect: 'zoom', scale: 1, x: .5, y: .5, ms: 1 }, { kind: 'stage', effect: 'zoom', scale: 1.12, x: .5, y: .29, ms: 120 },
    { kind: 'cue', effect: 'sfx:adv_princess_draw' },
    { kind: 'boss', name: '先代', text: 'じゃ、始めよっか。そういう役だから。', pre: 150 },
    { kind: 'boss', name: '先代', text: '……あなたなら、できるよ。', pace: 'slow' },
    { kind: 'stage', effect: 'zoom', scale: 1.26, x: .5, y: .34, ms: 140 },
    { kind: 'cue', effect: 'sfx:adv_princess_lunge' }, { kind: 'stage', effect: 'shake', power: 18, ms: 520 }, { kind: 'stage', effect: 'flash' },
    { kind: 'narration', text: '女が、踏み込んだ。' },
    { kind: 'cue', effect: 'blackout' },
    { kind: 'beat', ms: 300 },
  ],
}

// hollow-actor の前の版の台本 (開発版 ?twinsAdv=old)。 #7 の戦闘前は、剣舞 (#6) 戦で Loop「拍手を返してあげる」を選んだ場合だけ
//   1行 (前の版の役者が剣舞の口調を借りて拍手を打つ真似をする) が追加される (ワークブック #7 節)。
const HOLLOW_ACTOR_PREBATTLE_BASE: AdvLine[] = [
  { kind: 'narration', text: 'ラ、ラ、ララ——' },
  { kind: 'narration', text: '最後の、音が、消えた。' },
  { kind: 'narration', text: '舞台の、奥から、足音。' },
  { kind: 'narration', text: '仮面だけの、誰かが、現れた。' },
  { kind: 'narration', text: '顔のない、人だった。' },
  { kind: 'boss', name: '空っぽ', text: '……あ。' },
  { kind: 'boss', name: '空っぽ', text: '……あなた。来て、くれたのね。' },
  { kind: 'boss', name: '空っぽ', text: '——何者だ。主の御前であるぞ。' },
  { kind: 'boss', name: '空っぽ', text: 'カァッ……ヨアケ、ヲ……' },
  { kind: 'boss', name: '空っぽ', text: '……ラ、ラ、ララ……' },
]
const HOLLOW_ACTOR_CLAP_LINE: AdvLine = { kind: 'boss', name: '空っぽ', text: '——パチ、、パチ、、' }
const HOLLOW_ACTOR_TAIL_LINE: AdvLine = { kind: 'lunaire', text: '……今の、聞いたことがある。' }

function getHollowActorPreBattleLines(campaign: CampaignState): AdvLine[] {
  const lines = [...HOLLOW_ACTOR_PREBATTLE_BASE]
  if (campaign.choicesByBossId?.['blade-dancer'] === 'loop') lines.push(HOLLOW_ACTOR_CLAP_LINE)
  lines.push(HOLLOW_ACTOR_TAIL_LINE)
  return lines
}

// 先代の部屋 B「王がいるはずの部屋に、先代がいた」(11-01・作者の答え「直してから入れる」で配布版の既定。前の台本は開発版 ?princessRoom=old)。
//   作者の答え B (答えるページ 9LPWp3UqToMsKcAmewhnbH・db answers/princess-king-absent-1030)：影を王だと思って構える →「月喰いの王は？」
//   → 先代が「あの人のために来たの、好きなの」と茶化す → 否定 → 倒しに来た理由 →「じゃあ、あなたは誰なの」→ 名乗らずに「待ってたんだよ。ずっと。」へ戻る。
//   配布版の台本の中で、 影の「中央で、女が座って待っていた。」から「待ってたんだよ。ずっと。」の手前までを差し替える。 声の行は足さない (adv_voice_v2 の順がずれないように)。
//   記録＝docs/research/2026-11-01-princess-room-b.md
let princessRoom: 'now' | 'b' = 'b'
export function setPrincessRoom(v: string | null): void { princessRoom = v === 'old' ? 'now' : 'b' }
function princessRoomBLines(): AdvLine[] {
  const base = RAW_PREBATTLE['former-princess']
  const shadowAt = base.findIndex(l => l.kind === 'narration' && l.text === '中央で、女が座って待っていた。')
  const waitedAt = base.findIndex(l => l.kind === 'boss' && l.text === '待ってたんだよ。ずっと。')
  const comeAt = base.findIndex(l => l.kind === 'boss' && l.text === 'あ、来た来た。')
  if (shadowAt < 0 || comeAt < shadowAt || waitedAt !== comeAt + 1) return base
  return [
    ...base.slice(0, shadowAt),
    { kind: 'narration', text: '中央に、誰かが座っていた。' },
    { kind: 'cue', effect: 'sfx:adv_princess_draw' },
    { kind: 'narration', text: 'レイピアを、抜いた。', pre: 300 },
    { kind: 'lunaire', speech: 'spoken', text: '来たよ。あなたを、倒しに。' },
    ...base.slice(shadowAt + 1, comeAt + 1),   // 暗転 → 笑う先代の人物絵 → 跳ねる →「あ、来た来た。」
    { kind: 'lunaire', speech: 'inner', text: '……仮面が、無い……', pre: 400 },
    { kind: 'lunaire', speech: 'spoken', text: '月喰いの王は、どこ？' },
    { kind: 'boss', name: '先代', text: '王さま？' },
    { kind: 'stage', effect: 'hop' },
    { kind: 'boss', name: '先代', text: 'えー、わざわざ、あの人のために来たの？', pace: 'quick' },
    { kind: 'boss', name: '先代', text: 'もしかして、すきなの？' },
    { kind: 'lunaire', speech: 'spoken', text: '違う。すきなんかじゃない。', pace: 'quick' },
    // 先代は前の LUNAIRE なので、 王を好きでない・むしろ嫌いなのを分かっている (11-01 作者「まぁそうだよね的な」)
    { kind: 'boss', name: '先代', text: 'うん、知ってる。' },
    { kind: 'boss', name: '先代', text: 'むしろ、その反対だもんね。', pre: 200 },
    { kind: 'lunaire', speech: 'spoken', text: 'あの人のせいで、みんな、出られない。' },
    { kind: 'lunaire', speech: 'spoken', text: 'だから、倒しに来た。' },
    { kind: 'lunaire', speech: 'spoken', text: 'じゃあ、あなたは誰なの？', pre: 300 },
    ...base.slice(waitedAt),   // 名乗らずに「待ってたんだよ。ずっと。」→ レイピアを回す → 構えが同じ (答えの代わり)
  ]
}

export function getPreBattleScene(bossId: string, campaign: CampaignState): AdvScene | null {
  const scene = getPreBattleSceneRaw(bossId, campaign)
  return scene && applyAdvWordingFix(applyAdvVoiceV2(scene))
}
function getPreBattleSceneRaw(bossId: string, campaign: CampaignState): AdvScene | null {
  if (bossId === 'hollow-actor') {
    if (twinsAdvDraft) return { id: `prebattle:${bossId}`, lines: withTwinsArt(TWINS_PREBATTLE, TWINS_ART_FIRST.pre) }
    return { id: `prebattle:${bossId}`, lines: getHollowActorPreBattleLines(campaign) }
  }
  if (bossId === 'crowned-king-awoken') {
    return { id: `prebattle:${bossId}`, lines: getAwokenPreBattleLines(campaign) }
  }
  if (bossId === 'nameless-shade' && prologueEnabled) {
    return { id: `prebattle:${bossId}`, lines: prologueSfx ? PROLOGUE_LINES : PROLOGUE_LINES.filter(l => !PROLOGUE_SFX.has(l)) }
  }
  if (bossId === 'former-princess' && princessRoom === 'b') return { id: `prebattle:${bossId}`, lines: princessRoomBLines() }
  const lines = RAW_PREBATTLE[bossId]
  if (!lines) return null
  return { id: `prebattle:${bossId}`, lines }
}

// 冒頭ナレの反響 (エンドB/Loop-2 の締め = ループ再開)。 ⚠️ ゲーム冒頭ナレ本編は未実装 (別タスク)。
//   将来 冒頭ナレが実装されたら共有定数へ寄せる。 ワークブック エンドB [STAFF ROLL] 直前の3行。
const OPENING_NARRATION_ECHO: AdvLine[] = [
  { kind: 'narration', text: '城は古く、重かった。' },
  { kind: 'narration', text: '廊下に満ちているのは、怒りでも悲しみでもない。 何かを手放せずにいる者たちの、気配。' },
  { kind: 'lunaire', text: '——終わらせてあげないと。' },
]

// プロローグ (稽古の前)。 2026-10-29 下書き5版＝docs/research/2026-10-29-prologue-adv-v5.md（作者が通した台詞と演出）。
//   城の様子 (窓の人々・外套・王冠) は紙芝居で見せる (2026-10-30)。 絵だけの所は文字窓を消す (pictureOnly の beat・送り入力で早送りできる)。
//   名札「影」＝月喰いの王 (正体は戦う時まで出さない)。
//   効果音 (2026-10-30・Surge XT で作った・docs/research/2026-10-30-prologue-sfx.md)：作者が答えるまでは開発版 ?prologueSfx=on の時だけ鳴らす。
//   足した行は PROLOGUE_SFX に入れ、 off の時は行ごと外す (風に戻す cue は既存の行と同じ文字列なので、 文字列でなく行そのもので見分ける)。
let prologueEnabled = true
export function setPrologueEnabled(on: boolean): void { prologueEnabled = on }
let prologueSfx = false
export function setPrologueSfx(on: boolean): void { prologueSfx = on }
const PROLOGUE_SFX = new Set<AdvLine>()
const nu = (line: AdvLine): AdvLine => { PROLOGUE_SFX.add(line); return line }
const psfx = (name: string): AdvLine => nu({ kind: 'cue', effect: `sfx:adv_prologue_${name}` })
const PRO = '/adv/prologue/'
const pic = (ms: number): AdvLine => ({ kind: 'beat', ms, pictureOnly: true })
const collage = (page: AdvCollagePage): AdvLine => ({ kind: 'stage', effect: 'collage', page })
const PROLOGUE_LINES: AdvLine[] = [
  { kind: 'cue', effect: 'amb:adv_jester_wind' },
  { kind: 'narration', text: '夜が、明けなかった。' },
  { kind: 'picture', src: `${PRO}castle_hill.webp`, fadeMs: 1000 },
  // 城のまわりに常夜燈の青白い光が漂う (出どころは見せない・2026-10-30 作者)
  { kind: 'stage', effect: 'lampGlow', ms: 3000 },
  { kind: 'stage', effect: 'zoom', scale: 1, ms: 1 }, { kind: 'stage', effect: 'zoom', scale: 1.15, x: .49, y: .3, ms: 8000 },
  pic(1000), { kind: 'narration', text: '丘の上に、城があった。窓という窓に、灯りがともっていた。' },
  { kind: 'lunaire', speech: 'spoken', text: '……誰か、いる？' },
  { kind: 'voice', text: '——いるよ。みんな、いる。' },
  { kind: 'lunaire', speech: 'spoken', text: 'あなた、誰？' },
  { kind: 'narration', text: '振り向いても、坂道には誰もいなかった。', pre: 400 },
  { kind: 'voice', text: '——んー、道案内かな。あの城、見覚え、ない？' },
  { kind: 'lunaire', speech: 'spoken', text: '知らない。' },
  { kind: 'voice', text: '——知ってるはずだよ。あなたの城だもの。' },
  { kind: 'lunaire', speech: 'spoken', text: '私の……？' },
  { kind: 'voice', text: '——そう。あなたは、この城の姫。' },
  { kind: 'lunaire', speech: 'spoken', text: '姫なんて、知らない。' },
  { kind: 'narration', text: '何も、思い出せなかった。' },
  { kind: 'voice', text: '——ほら、見て。' },
  // 城の様子は紙芝居で見せる (2026-10-30 作者の答え＝切り貼り・白い縁の紙人形・部品を1つずつ描いてコードで動かす)。 ページの動きは src/adv_collage_view.ts。
  //   1 城が立ち窓が灯る (文字窓を消して絵だけ。 窓の中の騎士・花嫁は見せない＝10-30 夕 作者)
  collage('castle'), pic(2600),
  //   2 外套が覆いかぶさり、 門に錠
  psfx('lock'), collage('cloak'),
  { kind: 'voice', text: '——みんな、出られないんだよね。王さまの力で。' },
  { kind: 'lunaire', speech: 'spoken', text: '王さま？' },
  //   3 外套ごと城が運ばれ、 外套の頂点に王冠 (王さまは出さない・作者 10-30)
  collage('taken'),
  { kind: 'voice', text: '——自分でそう呼んでるだけ。外から来て、城ごと取っちゃったの。' },
  //   4 主人公の紙人形が立つ。 斬れば眠れる＝真ん中の窓の灯りが消える
  collage('princess'),
  { kind: 'lunaire', speech: 'spoken', text: '私に、出してあげられる？' },
  collage('sleep'),
  { kind: 'voice', text: '——出られはしないよ。でも、斬ってあげれば、眠れるの。' },
  //   5 灯りが戻り、 主人公は城に背を向ける
  collage('wake'),
  { kind: 'lunaire', speech: 'spoken', text: '斬るって、あの人たちを？' },
  collage('refuse'),
  { kind: 'lunaire', speech: 'spoken', text: 'しない。そんなこと。' },
  { kind: 'voice', text: '——うん。' },
  //   6 城へ向き直って呼ぶ。 中身のない吹き出しが窓へ飛び、 何も返らない (10-30 夕 作者「城に向かって呼ぶ」)
  psfx('bubble'), collage('call'),
  { kind: 'lunaire', speech: 'spoken', text: '私の声、聞こえる人、いない？' },
  psfx('bubble2'), collage('callAgain'),
  { kind: 'lunaire', speech: 'spoken', text: '誰かいるなら、出てきて。' },
  pic(1600),
  { kind: 'voice', text: '——意味ないよ。みんな、出られないからね。' },
  //   7 夜が明けないまま、 月だけが城のまわりをぐるぐる回る
  nu({ kind: 'cue', effect: 'amb:adv_prologue_ages' }), collage('ages'),
  { kind: 'lunaire', speech: 'spoken', text: 'みんな、ずっと出られないままなの？' },
  { kind: 'voice', text: '——月喰いの王が来てから、ずっと出られてないよ。' },
  // 紙芝居が風にめくれて飛び、 丘の城の絵へ戻る
  { kind: 'picture', src: `${PRO}castle_hill.webp`, fadeMs: 0 }, { kind: 'stage', effect: 'zoom', scale: 1, ms: 1 },
  { kind: 'stage', effect: 'lampGlow', ms: 1 }, nu({ kind: 'cue', effect: 'amb:adv_jester_wind' }), psfx('peel'), collage('peel'), pic(1300),
  { kind: 'lunaire', speech: 'spoken', text: '斬らずに、眠らせられない？' },
  { kind: 'voice', text: '——斬ってあげないと、眠れないの。' },
  // レイピアを胸元に寄せて迷い、 城を見つめて強く握りしめる (10-30 夕 作者の答えで新しく描いた2枚・城の窓を覗く寄りは置き換えた)
  { kind: 'picture', src: `${PRO}rapier_doubt.webp`, fadeMs: 800 }, { kind: 'stage', effect: 'zoom', scale: 1, ms: 1 },
  { kind: 'stage', effect: 'zoom', scale: 1.06, x: .3, y: .45, ms: 7000 },
  pic(1200), { kind: 'narration', text: 'レイピアを、胸元に寄せた。' },
  { kind: 'narration', text: '指が、すぐには閉じなかった。' },
  { kind: 'picture', src: `${PRO}rapier_resolve.webp`, fadeMs: 700 }, { kind: 'stage', effect: 'zoom', scale: 1, ms: 1 },
  { kind: 'stage', effect: 'zoom', scale: 1.08, x: .6, y: .4, ms: 6000 },
  pic(1400), { kind: 'narration', text: '城を、じっと見つめた。' },
  psfx('grip'), { kind: 'narration', text: '柄を、強く握りしめた。' },
  { kind: 'lunaire', speech: 'spoken', text: '……私が、救ってあげなくちゃ。' },
  { kind: 'voice', text: '——じゃあ、行こうか。' },
  { kind: 'cue', effect: 'blackout' }, { kind: 'beat', ms: 600 },
  { kind: 'picture', src: '/adv/knight/black.webp', fadeMs: 0 }, { kind: 'stage', effect: 'zoom', scale: 1, ms: 1 },
  { kind: 'picture', src: `${PRO}gate_moon.webp`, fadeMs: 600 },
  pic(600), { kind: 'narration', text: '城門は、開いたままだった。' },
  { kind: 'stage', effect: 'zoom', scale: 1.3, x: .505, y: .155, ms: 5000 },
  { kind: 'narration', text: '門の真上に、月が出ていた。' },
  { kind: 'picture', src: `${PRO}king_moon.webp`, fadeMs: 400 },
  { kind: 'narration', text: 'その月の前に、影が立った。', pre: 500 },
  { kind: 'narration', text: '外套の裾が、門の上で風になびいていた。' },
  { kind: 'stage', effect: 'shake', power: 3, ms: 1600 },
  { kind: 'boss', name: '影', text: '来たか、月の姫。', pre: 600, pace: 'slow' },
  { kind: 'narration', text: '顔のところだけ、白く浮いていた。笑った形のまま、動かない仮面だった。' },
  { kind: 'boss', name: '影', text: 'そなたのために、我が隣の席を空けておいた。', pace: 'slow' },
  { kind: 'boss', name: '影', text: 'ぜひ、参られよ。待っておるぞ。', pace: 'slow' },
  { kind: 'lunaire', speech: 'spoken', text: 'そこには、座らない。あなたを、倒しに行く。' },
  { kind: 'boss', name: '影', text: '座らぬなら、それもよい。', pace: 'slow' },
  // 消える時は、 テレビの砂嵐のようにザーッとなって一瞬暗転し、 月だけが映る (10-30 夕 作者)。 砂嵐は短く (10-30 夜 作者)。 砂嵐の音はぷつっと切れて終わり、 暗転の間は無音。
  // 月だけの絵＝王が立っていた同じ景色から王だけがいない絵 (門と月・寄りも王の時と同じ)。 月に寄った別の絵にはしない (10-30 夜 作者「月喰いの王がいない月だけの絵」)。
  psfx('static'), { kind: 'stage', effect: 'static', ms: 450 }, { kind: 'cue', effect: 'amb:off' }, pic(350),
  { kind: 'cue', effect: 'blackout' }, { kind: 'beat', ms: 350 },
  { kind: 'picture', src: '/adv/knight/black.webp', fadeMs: 0 }, { kind: 'stage', effect: 'zoom', scale: 1, ms: 1 }, pic(400),
  { kind: 'picture', src: `${PRO}gate_moon.webp`, fadeMs: 250 }, { kind: 'stage', effect: 'zoom', scale: 1.3, x: .505, y: .155, ms: 1 }, { kind: 'cue', effect: 'amb:adv_jester_wind' },
  pic(1600), { kind: 'narration', text: '月だけが、そこに残っていた。' },
  { kind: 'voice', text: '——あれが、月喰いの王。' },
  { kind: 'voice', text: '——席だって。モテるね、あなた。' },
  { kind: 'lunaire', speech: 'inner', text: 'どうして、私を隣に……' },
  { kind: 'cue', effect: 'blackout' }, { kind: 'beat', ms: 600 },
  { kind: 'picture', src: '/adv/knight/black.webp', fadeMs: 0 }, { kind: 'stage', effect: 'zoom', scale: 1, ms: 1 },
  { kind: 'picture', src: '/bg/shade-corridor.png', fadeMs: 800 }, { kind: 'cue', effect: 'amb:adv_knight_hall_air' },
  { ...OPENING_NARRATION_ECHO[0], pre: 800 } as AdvLine,
  ...OPENING_NARRATION_ECHO.slice(1),
  // このあと稽古。 稽古は声が説明する体で進むので、 その前置き (10-31 作者「ADVのこの稽古が始まる直前で、声が私が説明してあげる趣旨のテキストを入れて」)。
  { kind: 'voice', text: 'その前に、ちょっと練習しようか。戦い方は、私が説明してあげる。', pre: 600 },
  { kind: 'cue', effect: 'blackout' }, { kind: 'beat', ms: 1000 },
]

// 終幕の「5象徴」回収スライド用テーブル (Task5 render が読む)。 画像は未発注のため imageSrc は空。
//   画像到着時 (そざい/prompts/2026-07-01-ending-recap-symbols.md) は imageSrcBefore/After を埋めれば絵化する。
//   loopCaption = Loop側=既に整った状態 / trueCaption = True側=未完了(戻されなかった)状態。
export type EndingRecapSymbol = {
  name: string
  loopCaption: string
  trueCaption: string
  imageSrcBefore?: string
  imageSrcAfter?: string
}
export const ENDING_RECAP_SYMBOLS: EndingRecapSymbol[] = [
  { name: '王冠', loopCaption: '王冠が、玉座に戻っている。', trueCaption: '王冠は、転がったまま。' },
  { name: '時計', loopCaption: '時計は、婚礼の刻を指している。', trueCaption: '時計の針は、動いたまま。' },
  { name: '寝台', loopCaption: '空いた寝台が、埋まっている。', trueCaption: '寝台が、ひとつ、空いたまま。' },
  { name: '拍手', loopCaption: '拍手を待つ腕が、上がっている。', trueCaption: '腕は、落ちたまま。' },
  { name: '仮面', loopCaption: '仮面が、顔にかぶさっている。', trueCaption: '仮面は、見送られたまま。' },
]

// 終幕4種の本文。 演出は本文中の cue (lamp_focus / slide / flashback / world_out) を render が解釈。
//   各 cue の尺は直後の beat が持つ (activeCueBeat 方式)。
const RAW_ENDING: Record<EndingChoice, AdvLine[]> = {
  // エンドA: 残る (牢の番人)。 完全な確信で・詰まらず。 締めは先代と一文字違いの構文反復。
  stay: [
    { kind: 'narration', text: '迷いは、なかった。' },
    { kind: 'lunaire', text: '……私が、救う。' },
    { kind: 'lunaire', text: '次の姫を、私が、楽にしてあげる。' },
    { kind: 'narration', text: '円形の舞台に、静けさが降りた。' },
    { kind: 'narration', text: '倒れた女のそばに、レイピアが落ちていた。' },
    { kind: 'narration', text: '同じ形の、レイピア。' },
    { kind: 'narration', text: '拾おうと、手を伸ばした。' },
    { kind: 'narration', text: '指が、一瞬、止まった。' },
    { kind: 'narration', text: '……。' },
    { kind: 'narration', text: '思い直して、拾い上げた。' },
    { kind: 'lunaire', text: '……あなたも、こうやって、選んだのね。' },
    { kind: 'lunaire', text: '次に来る子を、救うために。' },
    { kind: 'narration', text: 'レイピアを、構えた。' },
    { kind: 'narration', text: '常夜燈が、舞台の隅で、変わらず、灯っていた。' },
    { kind: 'lunaire', text: '……次の幕は、私の番、ね。' },
    // 🎬 無数の光の川→常夜燈が呑む→暗転 (個別象徴は見せない)。
    { kind: 'cue', effect: 'lamp_focus' },
    { kind: 'beat', ms: 4000 },
  ],
  // エンドB: 降りる×しがみつき (Loop)。 完全な確信で。 5枚スライド(整った状態)→冒頭ナレ円環。
  loop: [
    { kind: 'lunaire', text: '……終わった。' },
    { kind: 'lunaire', text: 'これで、全部。' },
    { kind: 'narration', text: '円形の舞台に、静けさが降りた。' },
    { kind: 'narration', text: '倒れた女には、触れなかった。' },
    { kind: 'narration', text: '落ちていたレイピアにも、触れなかった。' },
    { kind: 'narration', text: '中央から、歩き出した。' },
    { kind: 'narration', text: '舞台の縁を、超えた。' },
    { kind: 'narration', text: '廊下が、見えた。' },
    { kind: 'lunaire', text: '……出れる。' },
    { kind: 'narration', text: '振り返らずに、廊下へ入った。' },
    // 🎬 5枚固定スライド (道中選択で整った象徴)。 placeholder=ENDING_RECAP_SYMBOLS のキャプション。
    { kind: 'cue', effect: 'slide' },
    { kind: 'beat', ms: 4500 },
    ...OPENING_NARRATION_ECHO,
  ],
  // エンドC: True (灯を消す)。 入りはBと同じ確信→気づきの温度差→世界消失。
  true: [
    { kind: 'lunaire', text: '……終わった。' },
    { kind: 'lunaire', text: 'これで、全部。' },
    { kind: 'narration', text: '円形の舞台に、静けさが降りた。' },
    { kind: 'narration', text: '倒れた女には、触れなかった。' },
    { kind: 'narration', text: '落ちていたレイピアにも、触れなかった。' },
    { kind: 'narration', text: '中央から、歩き出した。' },
    { kind: 'narration', text: '舞台の縁へ、向かった。' },
    { kind: 'narration', text: '——ふと、視界の隅に、青が、入った。' },
    { kind: 'narration', text: '常夜燈が、舞台の隅で、灯っていた。' },
    { kind: 'narration', text: '足が、止まった。' },
    { kind: 'lunaire', text: '……あれ。' },
    { kind: 'lunaire', text: 'ここにも、あった。' },
    { kind: 'narration', text: '見つめた。' },
    { kind: 'lunaire', text: 'どの舞台にも、あった。' },
    { kind: 'lunaire', text: 'ずっと、消えなかった。' },
    { kind: 'lunaire', text: '……そうか。' },
    { kind: 'lunaire', text: 'あれが、続かせていた。' },
    { kind: 'narration', text: '常夜燈のもとへ、歩いた。' },
    // 🎬 道中Trueで選んだ5象徴を「未完了」で並べる (placeholder=trueCaption)。
    { kind: 'cue', effect: 'flashback' },
    { kind: 'beat', ms: 4000 },
    { kind: 'lunaire', text: '……何も、きれいに、終わらなかった。' },
    { kind: 'narration', text: '常夜燈の前に、立った。' },
    { kind: 'narration', text: '手を、伸ばした。' },
    { kind: 'lunaire', text: '……終わらせる。' },
    // 🎬 常夜燈消灯→世界そのものが消える→完全な暗転 (光の残りもない)。
    { kind: 'cue', effect: 'world_out' },
    { kind: 'beat', ms: 4500 },
  ],
  // エンドB「ループ-2」: 時間切れ・呑まれる。 LUNAIRE内語なし・主体は舞台/場所/時間。
  loop2: [
    { kind: 'narration', text: '5秒が、過ぎた。' },
    { kind: 'narration', text: 'LUNAIRE は、動かなかった。' },
    { kind: 'narration', text: '円形の舞台に、静けさが降りた。' },
    { kind: 'narration', text: '舞台の縁が、揺らいだ。' },
    { kind: 'narration', text: '廊下が、見えた。' },
    { kind: 'narration', text: '気づけば、廊下に、いた。' },
    // 🎬 5枚スライド (道中選択関係なく全部 常夜燈の光で戻される)。 placeholder=loopCaption。
    { kind: 'cue', effect: 'slide' },
    { kind: 'beat', ms: 4500 },
    ...OPENING_NARRATION_ECHO,
  ],
}

// 「残る」版7 (11-03 作者 final=ship・最後の口元の絵が 11-04 に通って配布版の既定になった)。 開発版 ?stayEnding=old で前の台本 (上の RAW_ENDING.stay)。
//   版6 への作者の答え (11-03・db answers/stay-draft6-fix-1103・記録 docs/research/2026-11-03-stay-draft7.md)：
//     1=A 繰り返しには光に包まれた後、 走馬灯の絵で初めて気づく (言葉で言わない)。
//     2=A 残る理由は、 ひとりで待っていた先代の代わりに待つ役を引き受けること。 「次の姫を自分の手で終わらせる」(11-01) は外れた。
//     3=A 仮の lamp_focus を外した。 最後の2行 (ADV_STAY_V2_TAIL) は口元だけ笑う絵 (10-31・lunaire_smile)。
//     4=A 「振り返ると、来た道はもう暗かった。」。 走馬灯は約12枚を1秒弱ずつ。
//   外側の絵は栄えていた頃の劇場を手彩色の古写真で (11-03 作者 style=a・style_go=a・now=b)。 人は手元・後ろ姿・遠い人影だけ。
//   終わりは ADV_STAY_V2_TAIL (src/adv_voice_v2.ts) がそのまま付く。
export const STAY_PICTURES = {
  ghostlight: '/adv/stay/old_ghostlight.webp',
  house: '/adv/stay/old_house.webp',
  curtain: '/adv/stay/old_curtain.webp',
  ropes: '/adv/stay/old_ropes.webp',
  mirror: '/adv/stay/old_mirror.webp',
  chair: '/adv/stay/old_chair.webp',
  marks: '/adv/stay/old_marks.webp',
  scripts: '/adv/stay/old_scripts.webp',
  castlist: '/adv/stay/old_castlist.webp',
  dresses: '/adv/stay/old_dresses.webp',
  rapiers: '/adv/stay/old_rapiers.webp',
  // 最後の2行で映す、 残った LUNAIRE の不敵な笑み (10-12 作者・11-03 3=A)。 走馬灯ではなく本人なので会話場面の LUNAIRE の画風で、
  //   ティアラを載せている (11-04 作者「画風がリアルになってるし、LUNAIREではない。」で古写真の old_smile を外した)。 両目は見せない (11-04 作者「両目とも見せない形にして」)。 構図は D (引き)。 口元は悪役顔にしない少しニヤけた笑み (11-04 作者)。 3 (m3) を土台にもう少し可愛く (11-04 作者「1,3にもう少し可愛さが欲しい」)。 頬に淡い血色の k2 で通った (11-04 作者 pick=k2)
  smile: '/adv/stay/lunaire_smile_k2.webp',
  page: '/adv/stay/old_page.webp',
} as const
// 走馬灯：栄えていた頃の劇場から、 同じ物が何度も並ぶ絵へ。 並ぶレイピアで、 さっき拾ったレイピアとつながる。 後ろほど速い
const STAY_FLASHBACK: [keyof typeof STAY_PICTURES, number][] = [
  ['house', 900], ['curtain', 850], ['ropes', 800], ['mirror', 800], ['chair', 800],
  ['marks', 700], ['scripts', 650], ['castlist', 650], ['dresses', 600], ['rapiers', 900],
]
const RAW_ENDING_STAY_DRAFT: AdvLine[] = [
  // 一 LUNAIRE のうち (理由＝ひとりで待っていた先代の代わりに、 私が待つ)
  { kind: 'narration', text: '円い舞台に静けさが降りた。' },
  { kind: 'narration', text: '女はもう動かなかった。' },
  { kind: 'lunaire', speech: 'inner', text: 'ずっと待ってた……？' },
  { kind: 'lunaire', speech: 'inner', text: '誰も頼んでないのに……' },
  { kind: 'narration', text: '女のそばにレイピアが落ちていた。', pre: 400 },
  { kind: 'narration', text: '同じ形のレイピア。' },
  { kind: 'narration', text: '拾おうとして、手が止まった。' },
  { kind: 'narration', text: 'それでも拾い上げた。' },
  { kind: 'lunaire', speech: 'spoken', text: 'もう待たなくていいよ。', pre: 400 },
  { kind: 'lunaire', speech: 'spoken', text: '今度は私がここで待つ。' },
  // 二 取り込まれる＝役が書き換わる。 灯の向こうに劇場の本当の姿 (絵だけ・文では名指ししない)
  // 常夜燈と黒は /adv/stay/ の写しを使う。 覚醒王・先代のフォルダの絵は読む順番 (src/asset_queue.ts) でそのボスの番まで止められ、 結末で出なかった (11-03)
  { kind: 'picture', src: '/adv/stay/blue_lamp.webp', fadeMs: 1200 },
  { kind: 'narration', text: '常夜燈がすぐそばで灯っていた。', pre: 600 },
  { kind: 'lunaire', speech: 'inner', text: 'こんなに近かった……？' },
  { kind: 'narration', text: '青い灯が足元まで伸びてきた。' },
  { kind: 'narration', text: '下がろうとして、足が動かなかった。' },
  { kind: 'picture', src: STAY_PICTURES.ghostlight, fadeMs: 1500 },
  { kind: 'narration', text: '灯の奥に、何か見えた。', pre: 400 },
  { kind: 'beat', ms: 1600, pictureOnly: true },
  ...STAY_FLASHBACK.flatMap(([k, ms]): AdvLine[] => [
    { kind: 'picture', src: STAY_PICTURES[k], fadeMs: 200 },
    { kind: 'beat', ms, pictureOnly: true },
  ]),
  // 頁の書き足しは読めない青い筆跡 (11-03 作者 page=a)。 書いてある言葉はここの文で出す
  { kind: 'picture', src: STAY_PICTURES.page, fadeMs: 1200 },
  { kind: 'narration', text: '一行だけ、青く光っていた。', pre: 600 },
  { kind: 'lunaire', speech: 'inner', text: '今度は私がここで待つ……', pace: 'slow' },
  // 「もう乾いていた」で、 書かれたのが今ではない (決意も台本の役だった) を見せる (11-03 版5・作者 ok)
  { kind: 'narration', text: 'さっき私が言った言葉だった。' },
  { kind: 'narration', text: 'インクはとうに乾いていた。', pre: 400 },
  { kind: 'picture', src: '/adv/stay/black.webp', fadeMs: 1500 },
  // 案A (11-03 作者 text=a)：戦いの前の先代 (RAW_PREBATTLE['former-princess']) と字も順も同じに声に出して数え、 同じ所で止まる
  { kind: 'narration', text: '振り返ると、来た道はもう暗かった。', pre: 600 },
  { kind: 'lunaire', speech: 'spoken', text: '獣の子でしょ。騎士さんでしょ。花嫁さん。', pace: 'quick' },
  { kind: 'lunaire', speech: 'spoken', text: 'お布団の人。起きちゃったワンちゃん。', pre: 400 },
  { kind: 'lunaire', speech: 'spoken', text: 'それから……えっと……。', pace: 'slow', pre: 600 },
  { kind: 'lunaire', speech: 'spoken', text: '……まあ、いっか。', pre: 300 },
  { kind: 'narration', text: '折りかけた指を、そのまま下ろした。' },
  // 三 先代になる。 引き受けたのは「次の私を待つ役」だったと分かり、 理由が道案内へ変わる
  { kind: 'narration', text: '頭の上にティアラが載っていた。', pre: 800 },
  { kind: 'lunaire', speech: 'spoken', text: '次の幕は私の番ね。', pace: 'slow' },
  { kind: 'narration', text: '扉の向こうで燭台がひとつずつ灯っていった。' },
  { kind: 'lunaire', speech: 'inner', text: '次に来る子を、ここで待って……' },
  { kind: 'beat', ms: 900 },
  { kind: 'lunaire', speech: 'inner', text: 'そういえば、声がしてた……' },
  { kind: 'lunaire', speech: 'spoken', text: '迷わないようにしてあげなきゃ。' },
  { kind: 'beat', ms: 1600 },
  // このあと ADV_STAY_V2_TAIL の2行が口元の絵の上に出る
  { kind: 'picture', src: STAY_PICTURES.smile, fadeMs: 1800 },
]
let stayEnding: 'now' | 'draft' = 'draft'
export function setStayEnding(v: string | null): void { stayEnding = v === 'old' ? 'now' : 'draft' }

export function getEndingScene(kind: EndingChoice): AdvScene {
  const lines = kind === 'stay' && stayEnding === 'draft' ? RAW_ENDING_STAY_DRAFT : RAW_ENDING[kind]
  return applyAdvWordingFix(applyAdvVoiceV2({ id: `ending:${kind}`, lines }))
}

// エンジン結合確認用 (B-3 までの仮)。 選択の awaken 加算が通ることを示す最小シーン。
export const SAMPLE_CHOICE_SCENE: AdvScene = {
  id: 'sample:choice',
  lines: [
    { kind: 'other', text: '……ありがとう、と聞こえた気がした。' },
    { kind: 'beat', ms: 400 },
  ],
  choice: {
    options: [
      { text: '最期を見送る', awaken: 1 },
      { text: '魂を貪る', awaken: 0 },
    ],
  },
}
